import React from 'react';
import { Component } from 'react';
import { Theme } from '../Theme';
import { TabsItemProps, TabsView } from './TabsView';
import { FilesArea } from './FilesArea';
import {
    DashboardCustomRole,
    DashboardFeature,
    DashboardFeatureAccess,
    DashboardFeatureAccessByRole,
    DashboardRole,
    CreateDashboardUserRequest,
    DashboardUserSignInMethod,
    DashboardUserVerificationResult,
    CommercialModel,
    LicenseUsageSummary,
    ProviderEntitlementSnapshot,
    SelfServicePlanCode,
    ReportTemplate,
    Therapist,
} from '../interfaces';
import { Button } from './Button';
import { TableRowEllipsisMenuButton } from './TableRowEllipsisMenuButton';
import { TeamMemberTimelineView, TeamMemberTimelineData } from './TeamMemberTimelineView';
import {
    CONFIGURABLE_DASHBOARD_FEATURES,
    getDashboardRole,
    getDashboardRoleLabel,
    isDashboardFeatureAvailableForProvider,
    isDashboardFeatureIncludedInPlan,
    isRootOrSuperRole,
} from '../DashboardAccess';
import { getRequiredAdditionalLicenses } from '../saas/licenseUsage';
import { getEligibleBillingOwnerCandidates } from '../saas/billingOwnership';
import { buildProviderBillingDetails } from '../saas/providerBilling';
import type {
    BillingInterval,
    ExistingProviderPremiumCheckoutRequest,
    ExistingProviderPremiumMigrationOfferState,
    ExistingProviderPremiumMigrationPreview,
} from '../saas/SaasApi';
import { isOrganisationRoute } from '../saas/DashboardMigrationReturn';
import { ProviderPaymentSummary } from './ProviderPaymentSummary';
import '../saas/PlanCatalogAdmin.css';

const MAX_ORGANISATION_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const ORGANISATION_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Use an app-rendered arrow because Safari's native select chrome controls the field height.
const SelectChevron = () => (
    <span style={styles.selectChevron} aria-hidden='true'>
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
            <polyline
                points='6 9 12 15 18 9'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
        </svg>
    </span>
);

export type Props = {
    organisationName: string;
    organisationLogoUrl?: string;
    dashboardUrl: string;
    heatmapUrl?: string;
    userId: string;
    healthcareProviderId: string;
    therapists: Array<Therapist>;
    currentTherapistId: string;
    messagingReplyToTherapistId?: string;
    teamTimeline?: TeamMemberTimelineData;
    reportTemplates: Array<ReportTemplate>;
    canViewAnalyticsAndHeatmap: boolean;
    canViewTeam: boolean;
    canViewReportTemplates: boolean;
    canManageTeam: boolean;
    canManageOrganisationProfile: boolean;
    commercialModel?: CommercialModel;
    planCode?: SelfServicePlanCode;
    entitlement?: ProviderEntitlementSnapshot;
    licenseUsage?: LicenseUsageSummary;
    allowedDashboardFeatures: Array<DashboardFeature>;
    canManageBilling: boolean;
    dashboardRole: DashboardRole;
    dashboardRoles: Array<{ id: DashboardRole; label: string }>;
    assignableDashboardRoles: Array<{ id: DashboardRole; label: string }>;
    dashboardFeatures: Array<{ id: DashboardFeature; label: string }>;
    dashboardFeatureAccess: DashboardFeatureAccessByRole;
    dashboardFeatureAvailability?: DashboardFeatureAccess;
    canConfigureDashboardFeatureAvailability?: boolean;
    dashboardCustomRoles: Array<DashboardCustomRole>;
    canConfigureDashboardAccess: boolean;
    canCreateCustomRoles: boolean;
    onCreateDashboardUser: (request: CreateDashboardUserRequest) => Promise<void>;
    onResendDashboardUserVerificationEmail: (therapistId: string) => Promise<DashboardUserVerificationResult>;
    onUpdateTherapist: (therapistId: string, name: string, role: DashboardRole) => Promise<void>;
    onDeleteTherapist: (therapistId: string) => Promise<void>;
    onSetMessagingReplyTo: (therapistId: string) => Promise<void>;
    onUpdateOrganisationProfile: (name: string, file: File | null, removeLogo: boolean) => Promise<void>;
    onSetDashboardFeature: (role: DashboardRole, feature: DashboardFeature, enabled: boolean) => Promise<void>;
    onSetDashboardFeatureAvailability?: (feature: DashboardFeature, enabled: boolean) => Promise<void>;
    onCreateCustomRole: (name: string) => Promise<void>;
    onDeleteCustomRole: (role: DashboardRole) => Promise<void>;
    onOpenBillingPortal: () => Promise<void>;
    onChangeSubscriptionUnits: (subscriptionUnits: number) => Promise<void>;
    onTransferBillingOwnership: (newOwnerTherapistId: string) => Promise<void>;
    onGetExistingProviderPremiumMigrationPreview: () => Promise<ExistingProviderPremiumMigrationPreview>;
    onGetExistingProviderPremiumMigrationOfferState: () => Promise<ExistingProviderPremiumMigrationOfferState>;
    onSetExistingProviderPremiumMigrationOfferEnabled: (
        enabled: boolean,
        validationToken?: string
    ) => Promise<ExistingProviderPremiumMigrationOfferState>;
    onCreateExistingProviderPremiumCheckout: (
        request: ExistingProviderPremiumCheckoutRequest
    ) => Promise<void>;
    onCreateReportTemplate: (name: string, file: File | null, promptExtraInstructions: string) => Promise<void>;
    onUpdateReportTemplate: (
        reportTemplate: ReportTemplate,
        name: string,
        file: File | null,
        promptExtraInstructions: string
    ) => Promise<void>;
    onDeleteReportTemplate: (reportTemplate: ReportTemplate) => Promise<void>;
    onDownloadReportTemplate: (reportTemplate: ReportTemplate) => Promise<void>;
};

type State = {
    selectedTabId: string;
    selectedTeamMemberId: string | null;
    selectedTeamMemberProviderId: string | null;
    profileName: string;
    profileLogoFile: File | null;
    removeProfileLogo: boolean;
    profileDirty: boolean;
    isSavingProfile: boolean;
    profileError: string | null;
    filterText: string;
    showReportTemplateDialog: boolean;
    reportTemplateName: string;
    reportTemplateFile: File | null;
    reportTemplatePromptExtraInstructions: string;
    editingReportTemplate: ReportTemplate | null;
    hoveredReportTemplateId: string | null;
    busyReportTemplateId: string | null;
    isCreatingReportTemplate: boolean;
    hoveredTherapistId: string | null;
    showNewDashboardUserDialog: boolean;
    newDashboardUserName: string;
    newDashboardUserEmail: string;
    newDashboardUserRole: DashboardRole;
    newDashboardUserSignInMethod: DashboardUserSignInMethod;
    newDashboardUserTemporaryPassword: string;
    isCreatingDashboardUser: boolean;
    teamNotice: string | null;
    teamError: string | null;
    verificationEmailBusyTherapistId: string | null;
    messagingReplyToBusyTherapistId: string | null;
    verificationEmailCooldownTherapistIds: Array<string>;
    editingTherapist: Therapist | null;
    editingTherapistName: string;
    editingTherapistRole: DashboardRole;
    isSavingTherapist: boolean;
    deletingTherapistId: string | null;
    busyDashboardFeatureKey: string | null;
    busyFeatureAvailability: DashboardFeature | null;
    featureAvailabilityError: string | null;
    busyCustomRoleId: string | null;
    billingActionId: string | null;
    requestedSubscriptionUnits: number;
    selectedBillingOwnerTherapistId: string;
    migrationPreview: ExistingProviderPremiumMigrationPreview | null;
    migrationPreviewLoading: boolean;
    migrationError: string | null;
    migrationOfferState: ExistingProviderPremiumMigrationOfferState | null;
    migrationOfferStateLoading: boolean;
    migrationOfferSettingLoading: boolean;
    migrationOfferError: string | null;
    migrationBillingOwnerTherapistId: string;
    migrationBillingInterval: BillingInterval;
    migrationSubscriptionUnits: number;
    migrationCheckoutLoading: boolean;
};

export class OrganisationView extends Component<Props, State> {
    private teamOperationGeneration: number = 0;
    private profileOperationGeneration: number = 0;
    private featureAvailabilityOperationGeneration: number = 0;
    private migrationOperationGeneration: number = 0;
    private migrationOfferOperationGeneration: number = 0;
    private verificationEmailCooldownTimers = new Map<string, ReturnType<typeof setTimeout>>();

    constructor(props: Props) {
        super(props);
        this.state = {
            // Open the billing tab when returning from Stripe's hosted pages.
            selectedTabId: isOrganisationRoute(window.location.pathname) ? 'plan-billing' : 'analytics',
            selectedTeamMemberId: null,
            selectedTeamMemberProviderId: null,
            profileName: props.organisationName,
            profileLogoFile: null,
            removeProfileLogo: false,
            profileDirty: false,
            isSavingProfile: false,
            profileError: null,
            filterText: '',
            showReportTemplateDialog: false,
            reportTemplateName: '',
            reportTemplateFile: null,
            reportTemplatePromptExtraInstructions: '',
            editingReportTemplate: null,
            hoveredReportTemplateId: null,
            busyReportTemplateId: null,
            isCreatingReportTemplate: false,
            hoveredTherapistId: null,
            showNewDashboardUserDialog: false,
            newDashboardUserName: '',
            newDashboardUserEmail: '',
            newDashboardUserRole: 'healthcare_professional',
            newDashboardUserSignInMethod: 'microsoft',
            newDashboardUserTemporaryPassword: '',
            isCreatingDashboardUser: false,
            teamNotice: null,
            teamError: null,
            verificationEmailBusyTherapistId: null,
            messagingReplyToBusyTherapistId: null,
            verificationEmailCooldownTherapistIds: [],
            editingTherapist: null,
            editingTherapistName: '',
            editingTherapistRole: 'healthcare_professional',
            isSavingTherapist: false,
            deletingTherapistId: null,
            busyDashboardFeatureKey: null,
            busyFeatureAvailability: null,
            featureAvailabilityError: null,
            busyCustomRoleId: null,
            billingActionId: null,
            requestedSubscriptionUnits: Math.max(1, props.licenseUsage?.subscriptionUnits || 1),
            selectedBillingOwnerTherapistId: '',
            migrationPreview: null,
            migrationPreviewLoading: false,
            migrationError: null,
            migrationOfferState: null,
            migrationOfferStateLoading: false,
            migrationOfferSettingLoading: false,
            migrationOfferError: null,
            migrationBillingOwnerTherapistId: '',
            migrationBillingInterval: 'monthly',
            migrationSubscriptionUnits: 1,
            migrationCheckoutLoading: false,
        };
    }

    componentDidMount() {
        if (this.isPlanBillingTabDisplayed()) {
            this.loadPremiumMigrationData();
        }
    }

    componentWillUnmount() {
        // Ignore pending responses after the Organisation screen has been removed.
        this.teamOperationGeneration += 1;
        this.featureAvailabilityOperationGeneration += 1;
        this.migrationOperationGeneration += 1;
        this.migrationOfferOperationGeneration += 1;
        this.clearVerificationEmailCooldowns();
    }

    componentDidUpdate(previousProps: Props) {
        const providerChanged = previousProps.healthcareProviderId !== this.props.healthcareProviderId;
        // Ignore availability writes that finish after a provider switch or privilege change.
        if (providerChanged || previousProps.dashboardRole !== this.props.dashboardRole ||
            previousProps.canConfigureDashboardFeatureAvailability !== this.props.canConfigureDashboardFeatureAvailability) {
            this.featureAvailabilityOperationGeneration += 1;
            this.setState({ busyFeatureAvailability: null, featureAvailabilityError: null });
        }
        // A member page cannot survive a provider switch, removed membership or revoked Team access.
        if (this.state.selectedTeamMemberId && (providerChanged || !this.props.canViewTeam ||
            !this.props.therapists.some((member) => member.id === this.state.selectedTeamMemberId &&
                member.healthcareProviderId === this.props.healthcareProviderId))) {
            this.setState({selectedTeamMemberId: null, selectedTeamMemberProviderId: null});
        }
        if (providerChanged || previousProps.licenseUsage?.subscriptionUnits !== this.props.licenseUsage?.subscriptionUnits) {
            const nextUnits = Math.max(1, this.props.licenseUsage?.subscriptionUnits || 1);
            if (this.state.requestedSubscriptionUnits !== nextUnits) {
                this.setState({ requestedSubscriptionUnits: nextUnits });
            }
        }
        // Never carry a provider's privileged form state into another provider or a revoked session.
        if (providerChanged || (previousProps.canManageTeam && !this.props.canManageTeam)) {
            this.teamOperationGeneration += 1;
            this.clearVerificationEmailCooldowns();
            this.setState({
                showNewDashboardUserDialog: false,
                newDashboardUserName: '',
                newDashboardUserEmail: '',
                newDashboardUserRole: 'healthcare_professional',
                newDashboardUserSignInMethod: 'microsoft',
                newDashboardUserTemporaryPassword: '',
                isCreatingDashboardUser: false,
                teamNotice: null,
                teamError: null,
                verificationEmailBusyTherapistId: null,
                messagingReplyToBusyTherapistId: null,
                verificationEmailCooldownTherapistIds: [],
                hoveredTherapistId: null,
                editingTherapist: null,
                editingTherapistName: '',
                editingTherapistRole: 'healthcare_professional',
                isSavingTherapist: false,
                deletingTherapistId: null,
                selectedBillingOwnerTherapistId: '',
            });
        }
        const migrationAccessChanged = previousProps.dashboardRole !== this.props.dashboardRole;
        if (providerChanged || migrationAccessChanged ||
            (previousProps.commercialModel !== this.props.commercialModel)) {
            this.migrationOperationGeneration += 1;
            this.migrationOfferOperationGeneration += 1;
            this.setState({
                migrationPreview: null,
                migrationPreviewLoading: false,
                migrationError: null,
                migrationOfferState: null,
                migrationOfferStateLoading: false,
                migrationOfferSettingLoading: false,
                migrationOfferError: null,
                migrationBillingOwnerTherapistId: '',
                migrationBillingInterval: 'monthly',
                migrationSubscriptionUnits: 1,
                migrationCheckoutLoading: false,
            }, () => {
                if (this.isPlanBillingTabDisplayed()) {
                    this.loadPremiumMigrationData();
                }
            });
        }
        if (providerChanged ||
            (previousProps.canManageOrganisationProfile && !this.props.canManageOrganisationProfile)) {
            this.profileOperationGeneration += 1;
            this.setState({
                profileName: this.props.organisationName,
                profileLogoFile: null,
                removeProfileLogo: false,
                profileDirty: false,
                isSavingProfile: false,
                profileError: null,
            });
        } else if (!this.state.profileDirty &&
            (previousProps.organisationName !== this.props.organisationName ||
                previousProps.organisationLogoUrl !== this.props.organisationLogoUrl) &&
            this.state.profileName !== this.props.organisationName) {
            this.setState({ profileName: this.props.organisationName });
        }
        if (previousProps.canManageBilling && !this.props.canManageBilling &&
            this.state.selectedBillingOwnerTherapistId) {
            this.setState({ selectedBillingOwnerTherapistId: '' });
        }
    }

    private makeTabsItem(label: string, id: string): TabsItemProps {
        return {
            label,
            id,
            onSelect: () => this.setState({ selectedTabId: id }, () => {
                if (id === 'plan-billing') {
                    this.loadPremiumMigrationData();
                }
            }),
        };
    }

    private isPlanBillingTabDisplayed(): boolean {
        const tabs = this.getTabs();
        const selectedIndex = Math.max(0, tabs.findIndex((tab) => tab.id === this.state.selectedTabId));
        return tabs[selectedIndex]?.id === 'plan-billing';
    }

    private getTabs(): Array<TabsItemProps> {
        // Only offer the Analytics and Heatmap tabs when the provider has those pages configured.
        const tabs: Array<TabsItemProps> = [];
        if (this.props.canViewAnalyticsAndHeatmap && this.props.dashboardUrl) {
            tabs.push(this.makeTabsItem('Analytics', 'analytics'));
        }
        if (this.props.canViewAnalyticsAndHeatmap && this.getSafeHeatmapUrl()) {
            tabs.push(this.makeTabsItem('Heatmap', 'heatmap'));
        }
        if (this.props.canViewTeam) {
            tabs.push(this.makeTabsItem('Team', 'team'));
        }
        tabs.push(this.makeTabsItem('Files', 'files'));
        if (this.props.canViewReportTemplates) {
            tabs.push(this.makeTabsItem('Report templates', 'report-templates'));
        }
        if (this.props.canConfigureDashboardAccess) {
            tabs.push(this.makeTabsItem('Roles & access', 'roles-access'));
        }
        tabs.push(this.makeTabsItem(
            this.props.commercialModel === 'self_service' ? 'Plan & billing' : 'Agreement',
            'plan-billing'
        ));
        if (this.props.canManageOrganisationProfile || this.canConfigureFeatureAvailability()) {
            tabs.push(this.makeTabsItem('Settings', 'profile'));
        }
        return tabs;
    }

    private renderContent(selectedTab: TabsItemProps) {
        switch (selectedTab.id) {
            case 'profile':
                return this.renderProfile();
            case 'analytics':
                return this.renderAnalytics();
            case 'heatmap':
                return this.renderHeatmap();
            case 'team':
                return this.renderTeam();
            case 'files':
                return this.renderFiles();
            case 'report-templates':
                return this.renderReportTemplates();
            case 'roles-access':
                return this.renderRolesAndAccess();
            case 'plan-billing':
                return this.renderPlanAndBilling();
            default:
                return null;
        }
    }

    private getSafeHeatmapUrl(): string | null {
        // Only embed http(s) heatmap pages; ignore anything else (e.g. javascript: URLs).
        const heatmapUrl = (this.props.heatmapUrl || '').trim();
        return /^https?:\/\//i.test(heatmapUrl) ? heatmapUrl : null;
    }

    private handleProfileNameChange = (name: string) => {
        const hasOtherChanges = !!this.state.profileLogoFile || this.state.removeProfileLogo;
        this.setState({
            profileName: name,
            profileDirty: hasOtherChanges || name !== this.props.organisationName,
            profileError: null,
        });
    };

    private handleProfileLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Reject unsupported files immediately; the server still verifies the decoded image bytes.
        const file = event.currentTarget.files?.[0] || null;
        if (!file) {
            return;
        }
        if (!ORGANISATION_LOGO_CONTENT_TYPES.includes(file.type)) {
            event.currentTarget.value = '';
            this.setState({ profileError: 'Choose a PNG, JPEG, or WebP logo.' });
            return;
        }
        if (file.size > MAX_ORGANISATION_LOGO_SIZE_BYTES) {
            event.currentTarget.value = '';
            this.setState({ profileError: 'Choose a logo no larger than 2 MB.' });
            return;
        }

        this.setState({
            profileLogoFile: file,
            removeProfileLogo: false,
            profileDirty: true,
            profileError: null,
        });
    };

    private handleRemoveProfileLogo = () => {
        this.setState({
            profileLogoFile: null,
            removeProfileLogo: true,
            profileDirty: true,
            profileError: null,
        });
    };

    private handleSaveProfile = async () => {
        const name = this.state.profileName.trim();
        if (!name || name.length > 200) {
            this.setState({ profileError: 'Enter an organisation name of 200 characters or fewer.' });
            return;
        }

        const operationGeneration = ++this.profileOperationGeneration;
        try {
            this.setState({ isSavingProfile: true, profileError: null });
            await this.props.onUpdateOrganisationProfile(
                name,
                this.state.profileLogoFile,
                this.state.removeProfileLogo
            );
            if (operationGeneration !== this.profileOperationGeneration ||
                !this.props.canManageOrganisationProfile) {
                return;
            }
            this.setState({
                profileName: name,
                profileLogoFile: null,
                removeProfileLogo: false,
                profileDirty: false,
                isSavingProfile: false,
                profileError: null,
            });
        } catch (error) {
            if (operationGeneration !== this.profileOperationGeneration) {
                return;
            }
            console.error('ORGANISATION_VIEW: handleSaveProfile(): Error updating profile:', error);
            this.setState({
                isSavingProfile: false,
                profileError: error instanceof Error ? error.message : 'The organisation profile could not be saved.',
            });
        }
    };

    private renderProfile() {
        const hasCurrentLogo = !!this.props.organisationLogoUrl && !this.state.removeProfileLogo;
        const saveDisabled = this.state.isSavingProfile || !this.state.profileDirty ||
            !this.state.profileName.trim() || this.state.profileName.trim().length > 200;

        return (
            <div style={styles.profileContainer}>
                {this.props.canManageOrganisationProfile && <div style={styles.profileCard}>
                    <div>
                        <h2 style={styles.sectionTitle}>Organisation profile</h2>
                        <p style={styles.rolesAccessHint}>
                            The name and logo are shown to your dashboard team and identify this organisation.
                        </p>
                    </div>
                    <div style={styles.profileField}>
                        <label htmlFor='organisation-profile-name' style={styles.label}>Organisation name</label>
                        <input
                            id='organisation-profile-name'
                            type='text'
                            value={this.state.profileName}
                            maxLength={200}
                            onChange={(event) => this.handleProfileNameChange(event.currentTarget.value)}
                            style={styles.input}
                            disabled={this.state.isSavingProfile}
                        />
                        <p style={styles.fieldHint}>Use the name that your team and families will recognise.</p>
                    </div>
                    <div style={styles.profileField}>
                        <span style={styles.label}>Organisation logo</span>
                        <div style={styles.logoEditorRow}>
                            {hasCurrentLogo ? (
                                <img
                                    src={this.props.organisationLogoUrl}
                                    alt={`${this.props.organisationName} logo`}
                                    style={styles.logoPreview}
                                />
                            ) : (
                                <div style={styles.logoNameFallback} aria-label='No organisation logo configured'>
                                    {this.state.profileName.trim() || 'Organisation'}
                                </div>
                            )}
                            <div style={styles.logoControls}>
                                <label style={styles.logoUploadLabel}>
                                    {hasCurrentLogo ? 'Replace logo' : 'Upload logo'}
                                    <input
                                        type='file'
                                        accept='image/png,image/jpeg,image/webp'
                                        onChange={this.handleProfileLogoChange}
                                        disabled={this.state.isSavingProfile}
                                        style={styles.visuallyHiddenFileInput}
                                        aria-label={hasCurrentLogo ? 'Replace organisation logo' : 'Upload organisation logo'}
                                    />
                                </label>
                                {hasCurrentLogo && (
                                    <button
                                        type='button'
                                        onClick={this.handleRemoveProfileLogo}
                                        disabled={this.state.isSavingProfile}
                                        style={styles.removeLogoButton}
                                    >
                                        Remove logo
                                    </button>
                                )}
                            </div>
                        </div>
                        {this.state.profileLogoFile && (
                            <p style={styles.logoSelection}>Selected: {this.state.profileLogoFile.name}</p>
                        )}
                        {this.state.removeProfileLogo && (
                            <p style={styles.logoSelection}>The current logo will be removed when you save.</p>
                        )}
                        <p style={styles.fieldHint}>PNG, JPEG, or WebP. Maximum file size 2 MB.</p>
                    </div>
                    {this.state.profileError && (
                        <div role='alert' style={styles.profileError}>{this.state.profileError}</div>
                    )}
                    <div style={styles.profileActions}>
                        <Button
                            onClick={() => void this.handleSaveProfile()}
                            label={this.state.isSavingProfile ? 'Saving...' : 'Save changes'}
                            type='primary'
                            disabled={saveDisabled}
                        />
                    </div>
                </div>}
                {this.renderFeatureAvailability()}
            </div>
        );
    }

    // Availability is restricted to platform administrators, even when profile editing is allowed.
    private canConfigureFeatureAvailability(): boolean {
        return isRootOrSuperRole(this.props.dashboardRole) &&
            !!this.props.canConfigureDashboardFeatureAvailability;
    }

    // Keep old provider responses from clearing a new provider's pending change or showing its errors.
    private handleFeatureAvailabilityChange = async (feature: DashboardFeature, enabled: boolean) => {
        if (!this.canConfigureFeatureAvailability() || !this.props.onSetDashboardFeatureAvailability ||
            this.state.busyFeatureAvailability) return;
        const providerId = this.props.healthcareProviderId;
        const operationGeneration = ++this.featureAvailabilityOperationGeneration;
        this.setState({ busyFeatureAvailability: feature, featureAvailabilityError: null });
        try {
            await this.props.onSetDashboardFeatureAvailability(feature, enabled);
        } catch (error) {
            if (operationGeneration !== this.featureAvailabilityOperationGeneration ||
                providerId !== this.props.healthcareProviderId || !this.canConfigureFeatureAvailability()) return;
            this.setState({
                featureAvailabilityError: error instanceof Error ? error.message : 'Feature availability could not be saved.',
            });
        } finally {
            if (operationGeneration === this.featureAvailabilityOperationGeneration &&
                providerId === this.props.healthcareProviderId && this.canConfigureFeatureAvailability()) {
                this.setState({ busyFeatureAvailability: null });
            }
        }
    };

    // Always list every switch so platform administrators can restore unavailable features.
    private renderFeatureAvailability() {
        if (!this.canConfigureFeatureAvailability()) return null;
        const teamViewUnavailable = this.props.dashboardFeatureAvailability?.organisation_team === false;
        return (
            <section style={styles.profileCard} aria-labelledby='organisation-feature-availability-title'>
                <div>
                    {/* Match the privileged-section label used in Tools. */}
                    <span className='catalog-admin__root-badge' style={{ marginBottom: '8px' }}>Root &amp; Super</span>
                    <h2 id='organisation-feature-availability-title' style={styles.sectionTitle}>Feature availability</h2>
                    <p style={styles.rolesAccessHint}>
                        Choose which features are available to this organisation. Unavailable features are disabled
                        for every role and hidden from Roles &amp; access. Saved role permissions are kept.
                    </p>
                </div>
                <div style={styles.featureAvailabilityList}>
                    {CONFIGURABLE_DASHBOARD_FEATURES.map((feature) => {
                        const requiresTeamView = feature.id === 'organisation_team_manage' && teamViewUnavailable;
                        const requiresClients = feature.id === 'client_plans' &&
                            this.props.dashboardFeatureAvailability?.clients === false;
                        return (
                            <div key={feature.id}>
                                <label style={styles.featureAvailabilityRow}>
                                    <input
                                        type='checkbox'
                                        checked={feature.id === 'client_plans' ?
                                            this.props.dashboardFeatureAvailability?.client_plans === true :
                                            this.props.dashboardFeatureAvailability?.[feature.id] !== false}
                                        onChange={(event) => void this.handleFeatureAvailabilityChange(
                                            feature.id, event.currentTarget.checked
                                        )}
                                        disabled={!!this.state.busyFeatureAvailability || requiresTeamView || requiresClients ||
                                            !this.props.onSetDashboardFeatureAvailability}
                                        aria-label={`${feature.label} available to this organisation`}
                                    />
                                    <span>{feature.label}</span>
                                    {this.state.busyFeatureAvailability === feature.id && (
                                        <span style={styles.accessSavingLabel} role='status'>Saving...</span>
                                    )}
                                </label>
                                {requiresTeamView && <p style={styles.fieldHint}>
                                    Managing the team also requires View organisation team.
                                </p>}
                                {requiresClients && <p style={styles.fieldHint}>
                                    Client plans also requires Clients page.
                                </p>}
                            </div>
                        );
                    })}
                </div>
                {this.state.featureAvailabilityError && (
                    <div role='alert' style={styles.profileError}>{this.state.featureAvailabilityError}</div>
                )}
            </section>
        );
    }

    private renderAnalytics() {
        if (!this.props.dashboardUrl) {
            return <div style={styles.dashboardNotSetUp}>The dashboard is not set up. Please contact the administrator.</div>;
        }

        return (
            <div style={styles.amplitudeDashboard}>
                <iframe
                    src={`${this.props.dashboardUrl}?authuser=${this.props.userId}`}
                    width='100%'
                    height='100%'
                    frameBorder='0'
                    title='Amplitude Analytics Dashboard'
                    allowFullScreen
                    style={styles.iframe}
                />
            </div>
        );
    }

    private renderHeatmap() {
        // The tab is only offered when a valid heatmap URL exists.
        const heatmapUrl = this.getSafeHeatmapUrl();
        if (!heatmapUrl) {
            return null;
        }

        return (
            <div style={styles.heatmap}>
                <iframe
                    src={heatmapUrl}
                    width='100%'
                    height='100%'
                    frameBorder='0'
                    title='Login locations heatmap'
                    allowFullScreen
                    style={styles.iframe}
                />
            </div>
        );
    }

    // Start a clean dashboard-user form with the ordinary professional role selected.
    private showNewDashboardUserDialog = () => {
        const defaultRole = this.props.assignableDashboardRoles.find(
            (role) => role.id === 'healthcare_professional'
        )?.id || this.props.assignableDashboardRoles[0]?.id || 'healthcare_professional';
        const currentSubscriptionUnits = this.props.licenseUsage?.subscriptionUnits ||
            this.props.entitlement?.subscriptionUnits || 1;
        this.setState({
            showNewDashboardUserDialog: true,
            newDashboardUserName: '',
            newDashboardUserEmail: '',
            newDashboardUserRole: defaultRole,
            newDashboardUserSignInMethod: 'microsoft',
            newDashboardUserTemporaryPassword: '',
            isCreatingDashboardUser: false,
            requestedSubscriptionUnits: Math.min(20, currentSubscriptionUnits + 1),
        });
    };

    // Clear credentials and other sensitive form state whenever the dialog closes.
    private hideNewDashboardUserDialog = () => {
        this.setState({
            showNewDashboardUserDialog: false,
            newDashboardUserName: '',
            newDashboardUserEmail: '',
            newDashboardUserRole: 'healthcare_professional',
            newDashboardUserSignInMethod: 'microsoft',
            newDashboardUserTemporaryPassword: '',
            isCreatingDashboardUser: false,
        });
    };

    private clearVerificationEmailCooldowns() {
        this.verificationEmailCooldownTimers.forEach((timer) => clearTimeout(timer));
        this.verificationEmailCooldownTimers.clear();
    }

    private startVerificationEmailCooldown(therapistId: string) {
        const existingTimer = this.verificationEmailCooldownTimers.get(therapistId);
        if (existingTimer) clearTimeout(existingTimer);
        this.setState((state) => ({
            verificationEmailCooldownTherapistIds: Array.from(new Set([
                ...state.verificationEmailCooldownTherapistIds,
                therapistId,
            ])),
        }));
        const timer = setTimeout(() => {
            this.verificationEmailCooldownTimers.delete(therapistId);
            this.setState((state) => ({
                verificationEmailCooldownTherapistIds:
                    state.verificationEmailCooldownTherapistIds.filter((id) => id !== therapistId),
            }));
        }, 60_000);
        this.verificationEmailCooldownTimers.set(therapistId, timer);
    }

    private handleResendDashboardUserVerification = async (therapist: Therapist) => {
        if (this.state.verificationEmailBusyTherapistId ||
            this.state.verificationEmailCooldownTherapistIds.includes(therapist.id)) {
            return;
        }
        const operationGeneration = this.teamOperationGeneration;
        this.setState({
            verificationEmailBusyTherapistId: therapist.id,
            teamError: null,
            teamNotice: null,
        });
        try {
            const result = await this.props.onResendDashboardUserVerificationEmail(therapist.id);
            if (operationGeneration !== this.teamOperationGeneration) return;
            if (result.alreadyVerified) {
                this.setState({
                    verificationEmailBusyTherapistId: null,
                    teamNotice: `${therapist.name}'s email is already verified.`,
                });
                return;
            }
            this.setState({
                verificationEmailBusyTherapistId: null,
                teamNotice: `Verification email sent to ${therapist.name}.`,
            });
            this.startVerificationEmailCooldown(therapist.id);
        } catch (error) {
            if (operationGeneration !== this.teamOperationGeneration) return;
            this.setState({
                verificationEmailBusyTherapistId: null,
                teamError: error instanceof Error ? error.message : 'Could not resend the verification email.',
            });
        }
    };

    // Save the provider's reply recipient without changing this member's account or role.
    private handleSetMessagingReplyTo = async (therapist: Therapist) => {
        if (!this.props.canManageTeam || !therapist.email?.trim() ||
            therapist.healthcareProviderId !== this.props.healthcareProviderId ||
            this.state.messagingReplyToBusyTherapistId ||
            this.props.messagingReplyToTherapistId === therapist.id) {
            return;
        }
        const operationGeneration = this.teamOperationGeneration;
        this.setState({
            messagingReplyToBusyTherapistId: therapist.id,
            teamError: null,
            teamNotice: null,
        });
        try {
            await this.props.onSetMessagingReplyTo(therapist.id);
            if (operationGeneration !== this.teamOperationGeneration) return;
            this.setState({
                messagingReplyToBusyTherapistId: null,
                teamNotice: `Replies to scheduled Messaging emails will go to ${therapist.name} (${therapist.email.trim()}).`,
            });
        } catch (error) {
            if (operationGeneration !== this.teamOperationGeneration) return;
            this.setState({
                messagingReplyToBusyTherapistId: null,
                teamError: error instanceof Error ? error.message : 'Could not set the Messaging reply-to address.',
            });
        }
    };

    // Mirror the first-login password policy before sending an account request.
    private isValidTemporaryPassword(password: string): boolean {
        return password.length >= 8 && password.length <= 128 &&
            /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password) &&
            /[!@#$%^&*(),.?":{}|<>]/.test(password);
    }

    // Delegate account creation so the server can authoritatively enforce Team permissions.
    private handleCreateDashboardUser = async () => {
        const {
            newDashboardUserName,
            newDashboardUserEmail,
            newDashboardUserRole,
            newDashboardUserSignInMethod,
            newDashboardUserTemporaryPassword,
        } = this.state;
        if (!newDashboardUserName.trim() || !newDashboardUserEmail.trim() || !newDashboardUserRole ||
            (newDashboardUserSignInMethod === 'email_password' &&
                !this.isValidTemporaryPassword(newDashboardUserTemporaryPassword))) {
            return;
        }
        if (this.props.commercialModel === 'self_service') {
            const seatLimit = this.props.licenseUsage?.dashboardSeatLimit ||
                this.props.entitlement?.dashboardSeatLimit || 0;
            const occupiedSeats = (this.props.licenseUsage?.dashboardSeatsUsed || 0) +
                (this.props.licenseUsage?.dashboardSeatsReserved || 0);
            if (seatLimit > 0 && occupiedSeats >= seatLimit) return;
        }

        const operationGeneration = this.teamOperationGeneration;
        try {
            this.setState({ isCreatingDashboardUser: true });
            await this.props.onCreateDashboardUser({
                name: newDashboardUserName.trim(),
                email: newDashboardUserEmail.trim(),
                role: newDashboardUserRole,
                signInMethod: newDashboardUserSignInMethod,
                ...(newDashboardUserSignInMethod === 'email_password' ? {
                    temporaryPassword: newDashboardUserTemporaryPassword,
                } : {}),
            });
            if (operationGeneration !== this.teamOperationGeneration) {
                return;
            }
            this.hideNewDashboardUserDialog();
            this.setState({
                teamNotice: newDashboardUserSignInMethod === 'email_password'
                    ? `Verification email sent to ${newDashboardUserEmail.trim()}. ` +
                        'Share the temporary password separately.'
                    : `${newDashboardUserEmail.trim()} can now sign in with Microsoft.`,
                teamError: null,
            });
        } catch (error) {
            if (operationGeneration !== this.teamOperationGeneration) {
                return;
            }
            console.error('ORGANISATION_VIEW: handleCreateDashboardUser(): Error creating dashboard user:', error);
            window.alert(error instanceof Error ? error.message : 'Could not create the dashboard user.');
            this.setState({ isCreatingDashboardUser: false });
        }
    };

    private showEditTherapistDialog = (therapist: Therapist) => {
        // Start the editor from the normalized role so legacy super records remain editable.
        this.setState({
            editingTherapist: therapist,
            editingTherapistName: therapist.name || '',
            editingTherapistRole: getDashboardRole(therapist),
            isSavingTherapist: false,
        });
    }

    private hideEditTherapistDialog = () => {
        this.setState({
            editingTherapist: null,
            editingTherapistName: '',
            editingTherapistRole: 'healthcare_professional',
            isSavingTherapist: false,
        });
    }

    private handleSaveTherapist = async () => {
        const { editingTherapist, editingTherapistName, editingTherapistRole } = this.state;
        if (!editingTherapist || !editingTherapistName.trim() || !editingTherapistRole) {
            return;
        }

        const operationGeneration = this.teamOperationGeneration;
        try {
            this.setState({ isSavingTherapist: true });
            await this.props.onUpdateTherapist(
                editingTherapist.id,
                editingTherapistName.trim(),
                editingTherapistRole
            );
            if (operationGeneration !== this.teamOperationGeneration) {
                return;
            }
            this.hideEditTherapistDialog();
        } catch (error) {
            if (operationGeneration !== this.teamOperationGeneration) {
                return;
            }
            console.error('ORGANISATION_VIEW: handleSaveTherapist(): Error updating dashboard user:', error);
            window.alert(error instanceof Error ? error.message : 'Could not update the dashboard user.');
            this.setState({ isSavingTherapist: false });
        }
    }

    private handleDeleteTherapist = async (therapist: Therapist) => {
        if (!this.props.canManageTeam || !window.confirm(
            `Delete ${therapist.name}? This removes their dashboard access and cannot be undone.`
        )) {
            return;
        }

        const operationGeneration = this.teamOperationGeneration;
        try {
            this.setState({ deletingTherapistId: therapist.id });
            await this.props.onDeleteTherapist(therapist.id);
        } catch (error) {
            if (operationGeneration !== this.teamOperationGeneration) {
                return;
            }
            console.error('ORGANISATION_VIEW: handleDeleteTherapist(): Error deleting dashboard user:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the dashboard user.');
        } finally {
            if (operationGeneration === this.teamOperationGeneration) {
                this.setState({ deletingTherapistId: null });
            }
        }
    };

    // Navigate by the typed Team identity; Client ids are never substituted for member ids.
    private openTeamMemberTimeline = (member: Therapist) => {
        if (!this.props.canViewTeam || member.healthcareProviderId !== this.props.healthcareProviderId) return;
        this.setState({
            selectedTeamMemberId: member.id,
            selectedTeamMemberProviderId: this.props.healthcareProviderId,
        });
    };

    private renderTeam() {
        const { therapists } = this.props;
        const { filterText } = this.state;
        const isSelfService = this.props.commercialModel === 'self_service';
        const dashboardSeatLimit = this.props.licenseUsage?.dashboardSeatLimit ||
            this.props.entitlement?.dashboardSeatLimit || 0;
        const dashboardSeatsUsed = this.props.licenseUsage?.dashboardSeatsUsed || 0;
        const dashboardSeatsReserved = this.props.licenseUsage?.dashboardSeatsReserved || 0;
        const dashboardSeatsConsumed = dashboardSeatsUsed + dashboardSeatsReserved;
        const hasSeatAllowance = isSelfService && dashboardSeatLimit > 0;
        const dashboardSeatLimitReached = hasSeatAllowance && dashboardSeatsConsumed >= dashboardSeatLimit;
        const currentSubscriptionUnits = this.props.licenseUsage?.subscriptionUnits ||
            this.props.entitlement?.subscriptionUnits || Math.max(1, dashboardSeatLimit);
        const isNewDashboardUserInvalid = !this.state.newDashboardUserName.trim() ||
            !this.state.newDashboardUserEmail.trim() || !this.state.newDashboardUserRole ||
            (this.state.newDashboardUserSignInMethod === 'email_password' &&
                !this.isValidTemporaryPassword(this.state.newDashboardUserTemporaryPassword)) ||
            dashboardSeatLimitReached;

        const filteredTherapists = [...therapists]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .filter((therapist) => {
                const searchText = filterText.toLowerCase();
                const roleLabel = getDashboardRoleLabel(
                    getDashboardRole(therapist),
                    this.props.dashboardCustomRoles
                );
                const membershipLabel = this.getMembershipLabel(therapist).toLowerCase();
                return (therapist.name || '').toLowerCase().includes(searchText) ||
                    roleLabel.toLowerCase().includes(searchText) ||
                    membershipLabel.includes(searchText);
            });

        return (
            <div style={styles.teamContainer}>
                {/* search box */}
                <div style={styles.searchContainer}>
                    <input
                        type='text'
                        value={filterText}
                        onChange={(e) => this.setState({ filterText: e.target.value })}
                        placeholder='Search team...'
                        style={{ 
                            flex: 1,
                            padding: '12px 16px',
                            margin: '0 0 0 0', // top right bottom left
                            borderRadius: '8px',
                            border: Theme.Colours.border,
                        }}
                    />
                    {this.props.canManageTeam && (
                        <Button
                            onClick={this.showNewDashboardUserDialog}
                            label='New dashboard user'
                            icon={require('./assets/icons/add_selected.svg').default}
                            type='primary'
                        />
                    )}
                </div>

                {this.state.teamNotice && (
                    <div role='status' style={styles.seatUsageSummary}>{this.state.teamNotice}</div>
                )}
                {this.state.teamError && (
                    <div role='alert' style={styles.seatLimitAlert}>{this.state.teamError}</div>
                )}
                
                {/* therapists table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.headerCell}>Name</th>
                                <th style={styles.headerCell}>Role</th>
                                <th style={styles.headerCell}>Sign-in</th>
                                {this.props.commercialModel === 'self_service' && (
                                    <th style={styles.headerCell}>Status</th>
                                )}
                                <th style={styles.actionsHeaderCell}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTherapists.length === 0 ? (
                                <tr>
                                    <td
                                        style={styles.emptyTableCell}
                                        colSpan={this.props.commercialModel === 'self_service' ? 5 : 4}
                                    >
                                        No team members found.
                                    </td>
                                </tr>
                            ) : filteredTherapists.map((therapist) => {
                                const therapistRole = getDashboardRole(therapist);
                                const isHovered = this.state.hoveredTherapistId === therapist.id;
                                const canEditTherapist = this.props.canManageTeam &&
                                    this.props.assignableDashboardRoles.some((role) => role.id === therapistRole);
                                // Reply routing can use any member with an email, including privileged roles and yourself.
                                const canSetMessagingReplyTo = this.props.canManageTeam && !!therapist.email?.trim();
                                const isMessagingReplyTo = this.props.messagingReplyToTherapistId === therapist.id &&
                                    !!therapist.email?.trim();
                                const verificationPending = therapist.signInMethod === 'email_password' &&
                                    therapist.emailVerified !== true;
                                const verificationBusy = this.state.verificationEmailBusyTherapistId === therapist.id;
                                const verificationCoolingDown =
                                    this.state.verificationEmailCooldownTherapistIds.includes(therapist.id);
                                return (
                                    <tr
                                        key={therapist.id}
                                        style={{
                                            backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => this.openTeamMemberTimeline(therapist)}
                                        onMouseEnter={() => this.setState({ hoveredTherapistId: therapist.id })}
                                        onMouseLeave={() => {
                                            if (this.state.hoveredTherapistId === therapist.id) {
                                                this.setState({ hoveredTherapistId: null });
                                            }
                                        }}
                                    >
                                        <td style={styles.tableCell}>
                                            <button
                                                type='button'
                                                aria-label={`Open timeline for ${therapist.name}`}
                                                style={styles.teamMemberNameButton}
                                            >
                                                {therapist.name}
                                            </button>
                                            {isMessagingReplyTo && (
                                                <div style={styles.messagingReplyToDetails}>
                                                    <span style={styles.teamVerificationStatus}>Messaging reply-to</span>
                                                    <span>{therapist.email?.trim()}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td style={styles.tableCell}>
                                            {getDashboardRoleLabel(therapistRole, this.props.dashboardCustomRoles)}
                                        </td>
                                        <td style={styles.tableCell}>
                                            <span style={verificationPending ?
                                                styles.teamVerificationPending : styles.teamVerificationStatus}>
                                                {therapist.signInMethod === 'email_password' ?
                                                    (verificationPending ? 'Email verification pending' : 'Email verified') :
                                                    therapist.signInMethod === 'microsoft' ? 'Microsoft sign-in' : 'Legacy account'}
                                            </span>
                                        </td>
                                        {this.props.commercialModel === 'self_service' && (
                                            <td style={styles.tableCell}>
                                                <span style={therapist.membershipStatus === 'suspended' ?
                                                    styles.teamStatusSuspended : styles.teamStatus}>
                                                    {this.getMembershipLabel(therapist)}
                                                </span>
                                                {therapist.billingRole === 'owner' && (
                                                    <span style={styles.billingOwnerLabel}>Billing owner</span>
                                                )}
                                            </td>
                                        )}
                                        <td style={styles.actionsCell}>
                                            {(canEditTherapist || canSetMessagingReplyTo) && (
                                                <TableRowEllipsisMenuButton
                                                    ariaLabel={`Options for ${therapist.name}`}
                                                    isVisible={isHovered}
                                                    options={[
                                                        ...(canEditTherapist ? [{
                                                            label: 'Edit',
                                                            onClick: () => this.showEditTherapistDialog(therapist),
                                                        }] : []),
                                                        ...(canSetMessagingReplyTo ? [{
                                                            label: this.state.messagingReplyToBusyTherapistId === therapist.id ?
                                                                'Saving Messaging reply-to…' : 'Set as Messaging reply-to',
                                                            onClick: () => void this.handleSetMessagingReplyTo(therapist),
                                                            disabled: !!this.state.messagingReplyToBusyTherapistId || isMessagingReplyTo,
                                                        }] : []),
                                                        ...(canEditTherapist && verificationPending ? [{
                                                            label: verificationBusy ? 'Sending verification email…' :
                                                                verificationCoolingDown ? 'Verification email sent — wait 1 minute' :
                                                                    'Resend verification email',
                                                            onClick: () => void this.handleResendDashboardUserVerification(therapist),
                                                            disabled: verificationBusy || verificationCoolingDown,
                                                        }] : []),
                                                        ...(canEditTherapist && therapist.id !== this.props.currentTherapistId ? [{
                                                            label: this.state.deletingTherapistId === therapist.id ?
                                                                'Deleting...' : 'Delete',
                                                            onClick: () => void this.handleDeleteTherapist(therapist),
                                                            disabled: !!this.state.deletingTherapistId,
                                                            destructive: true,
                                                        }] : []),
                                                    ]}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {this.props.canManageTeam && this.state.showNewDashboardUserDialog && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>New dashboard user</h3>
                            {hasSeatAllowance && (
                                <div
                                    role={dashboardSeatLimitReached ? 'alert' : 'status'}
                                    style={dashboardSeatLimitReached ? styles.seatLimitAlert : styles.seatUsageSummary}
                                >
                                    <strong>
                                        {dashboardSeatsConsumed} of {dashboardSeatLimit} dashboard seat
                                        {dashboardSeatLimit === 1 ? '' : 's'} used
                                    </strong>
                                    {dashboardSeatsReserved > 0 && (
                                        <span>
                                            Includes {dashboardSeatsReserved} pending dashboard user
                                            {dashboardSeatsReserved === 1 ? '' : 's'}.
                                        </span>
                                    )}
                                    {dashboardSeatLimitReached && (
                                        <span>No seat is available for this new user.</span>
                                    )}
                                </div>
                            )}
                            {dashboardSeatLimitReached && this.props.planCode === 'premium' && (
                                <div style={styles.seatCapacityActions}>
                                    {this.props.canManageBilling && currentSubscriptionUnits < 20 ? (
                                        <>
                                            <p style={styles.fieldHint}>
                                                Each additional Premium unit adds one dashboard seat and 50 annual family licences.
                                                Stripe will bill the prorated increase.
                                            </p>
                                            <div style={styles.seatCapacityControls}>
                                                <label
                                                    htmlFor='new-user-subscription-units'
                                                    style={{ ...styles.label, flex: '1 1 160px' }}
                                                >
                                                    New subscription capacity
                                                </label>
                                                <input
                                                    id='new-user-subscription-units'
                                                    type='number'
                                                    min={currentSubscriptionUnits + 1}
                                                    max={20}
                                                    step={1}
                                                    value={Math.max(
                                                        currentSubscriptionUnits + 1,
                                                        this.state.requestedSubscriptionUnits
                                                    )}
                                                    onChange={(event) => this.setState({
                                                        requestedSubscriptionUnits: Math.max(
                                                            currentSubscriptionUnits + 1,
                                                            Math.min(20, Number(event.target.value) || currentSubscriptionUnits + 1)
                                                        ),
                                                    })}
                                                    style={styles.capacityInput}
                                                    disabled={this.state.billingActionId === 'capacity'}
                                                />
                                                <Button
                                                    onClick={() => void this.handleSubscriptionUnitChange()}
                                                    label={this.state.billingActionId === 'capacity' ?
                                                        'Increasing…' : 'Increase capacity'}
                                                    type='primary'
                                                    disabled={!!this.state.billingActionId}
                                                />
                                            </div>
                                        </>
                                    ) : currentSubscriptionUnits >= 20 ? (
                                        <a
                                            href='mailto:support@superpenguin.com?subject=Enterprise%20SuperPenguin%20capacity'
                                            style={styles.capacityLink}
                                        >
                                            Talk to us about capacity above 20 seats
                                        </a>
                                    ) : (
                                        <p style={styles.fieldHint}>
                                            Ask your organisation’s billing owner to increase Premium capacity.
                                        </p>
                                    )}
                                </div>
                            )}
                            {dashboardSeatLimitReached && this.props.planCode === 'starter' && (
                                <div style={styles.seatCapacityActions}>
                                    {this.props.canManageBilling ? (
                                        <a href='/plans' style={styles.capacityLink}>Upgrade to Premium</a>
                                    ) : (
                                        <p style={styles.fieldHint}>
                                            Ask your organisation’s billing owner to upgrade to Premium.
                                        </p>
                                    )}
                                </div>
                            )}
                            <div style={styles.formField}>
                                <label htmlFor='new-dashboard-user-name' style={styles.label}>Name</label>
                                <input
                                    id='new-dashboard-user-name'
                                    type='text'
                                    value={this.state.newDashboardUserName}
                                    onChange={(event) => this.setState({ newDashboardUserName: event.target.value })}
                                    style={styles.input}
                                    disabled={this.state.isCreatingDashboardUser}
                                    autoFocus
                                />
                            </div>
                            <div style={styles.formField}>
                                <label htmlFor='new-dashboard-user-email' style={styles.label}>Email</label>
                                <input
                                    id='new-dashboard-user-email'
                                    type='email'
                                    value={this.state.newDashboardUserEmail}
                                    onChange={(event) => this.setState({ newDashboardUserEmail: event.target.value })}
                                    style={styles.input}
                                    disabled={this.state.isCreatingDashboardUser}
                                    autoComplete='off'
                                />
                            </div>
                            <div style={styles.formField}>
                                <label htmlFor='new-dashboard-user-sign-in-method' style={styles.label}>Sign-in method</label>
                                <div style={styles.selectControl}>
                                    <select
                                        id='new-dashboard-user-sign-in-method'
                                        value={this.state.newDashboardUserSignInMethod}
                                        onChange={(event) => this.setState({
                                            newDashboardUserSignInMethod: event.target.value as DashboardUserSignInMethod,
                                            newDashboardUserTemporaryPassword: '',
                                        })}
                                        style={{ ...styles.input, ...styles.selectInput }}
                                        disabled={this.state.isCreatingDashboardUser}
                                    >
                                        <option value='microsoft'>Sign in with Microsoft</option>
                                        <option value='email_password'>Email and password</option>
                                    </select>
                                    <SelectChevron />
                                </div>
                                <p style={styles.fieldHint}>
                                    {this.state.newDashboardUserSignInMethod === 'microsoft' ?
                                        'Creates the Therapists record only. Microsoft creates the Authentication account at first sign-in.' :
                                        'Creates both records and sends a verification email. Share the temporary password separately.'}
                                </p>
                            </div>
                            {this.state.newDashboardUserSignInMethod === 'email_password' && (
                                <div style={styles.formField}>
                                    <label htmlFor='new-dashboard-user-temporary-password' style={styles.label}>
                                        Temporary password
                                    </label>
                                    <input
                                        id='new-dashboard-user-temporary-password'
                                        type='password'
                                        value={this.state.newDashboardUserTemporaryPassword}
                                        onChange={(event) => this.setState({
                                            newDashboardUserTemporaryPassword: event.target.value,
                                        })}
                                        style={styles.input}
                                        disabled={this.state.isCreatingDashboardUser}
                                        autoComplete='new-password'
                                    />
                                    <p style={styles.fieldHint}>
                                        Use 8+ characters with upper and lowercase letters, a number, and a special character.
                                        They will change it after their first sign-in.
                                    </p>
                                </div>
                            )}
                            <div style={styles.formField}>
                                <label htmlFor='new-dashboard-user-role' style={styles.label}>Role</label>
                                <div style={styles.selectControl}>
                                    <select
                                        id='new-dashboard-user-role'
                                        value={this.state.newDashboardUserRole}
                                        onChange={(event) => this.setState({ newDashboardUserRole: event.target.value })}
                                        style={{ ...styles.input, ...styles.selectInput }}
                                        disabled={this.state.isCreatingDashboardUser}
                                    >
                                        {this.props.assignableDashboardRoles.map((role) => (
                                            <option key={role.id} value={role.id}>{role.label}</option>
                                        ))}
                                    </select>
                                    <SelectChevron />
                                </div>
                            </div>
                            <div style={styles.dialogButtons}>
                                <Button
                                    onClick={this.hideNewDashboardUserDialog}
                                    label='Cancel'
                                    type='secondary'
                                    disabled={this.state.isCreatingDashboardUser}
                                />
                                <Button
                                    onClick={this.handleCreateDashboardUser}
                                    label={this.state.isCreatingDashboardUser ? 'Creating...' : 'Create'}
                                    type='primary'
                                    disabled={this.state.isCreatingDashboardUser || isNewDashboardUserInvalid}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {this.props.canManageTeam && this.state.editingTherapist && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>Edit dashboard user</h3>
                            <div style={styles.formField}>
                                <label style={styles.label}>Name</label>
                                <input
                                    type='text'
                                    value={this.state.editingTherapistName}
                                    onChange={(event) => this.setState({ editingTherapistName: event.target.value })}
                                    style={styles.input}
                                    disabled={this.state.isSavingTherapist}
                                    autoFocus
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.label}>Role</label>
                                <select
                                    value={this.state.editingTherapistRole}
                                    onChange={(event) => this.setState({ editingTherapistRole: event.target.value })}
                                    style={styles.input}
                                    disabled={this.state.isSavingTherapist}
                                >
                                    {this.props.assignableDashboardRoles.map((role) => (
                                        <option key={role.id} value={role.id}>{role.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.dialogButtons}>
                                <Button
                                    onClick={this.hideEditTherapistDialog}
                                    label='Cancel'
                                    type='secondary'
                                    disabled={this.state.isSavingTherapist}
                                />
                                <Button
                                    onClick={this.handleSaveTherapist}
                                    label={this.state.isSavingTherapist ? 'Saving...' : 'Save'}
                                    type='primary'
                                    disabled={this.state.isSavingTherapist || !this.state.editingTherapistName.trim()}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private getMembershipLabel(therapist: Therapist): string {
        // Older team records predate membership states and remain active by default.
        if (therapist.membershipStatus === 'suspended') {
            return therapist.suspendedReason === 'plan_seat_limit' ? 'Suspended — seat limit' : 'Suspended';
        }
        if (therapist.membershipStatus === 'pending') {
            return 'Pending';
        }
        return 'Active';
    }

    private renderFiles() {
        return <FilesArea healthcareProviderId={this.props.healthcareProviderId} />;
    }

    private handleDashboardFeatureChange = async (
        role: DashboardRole,
        feature: DashboardFeature,
        enabled: boolean
    ) => {
        const busyKey = `${role}:${feature}`;
        try {
            this.setState({ busyDashboardFeatureKey: busyKey });
            await this.props.onSetDashboardFeature(role, feature, enabled);
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleDashboardFeatureChange(): Error updating access:', error);
            window.alert(error instanceof Error ? error.message : 'Could not update dashboard access.');
        } finally {
            this.setState({ busyDashboardFeatureKey: null });
        }
    }

    private handleCreateCustomRole = async () => {
        const name = window.prompt('Name for the new custom role:')?.trim();
        if (!name) {
            return;
        }

        try {
            this.setState({ busyCustomRoleId: 'new' });
            await this.props.onCreateCustomRole(name);
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleCreateCustomRole(): Error creating role:', error);
            window.alert(error instanceof Error ? error.message : 'Could not create the custom role.');
        } finally {
            this.setState({ busyCustomRoleId: null });
        }
    }

    private handleDeleteCustomRole = async (role: { id: DashboardRole; label: string }) => {
        if (!window.confirm(`Delete the custom role "${role.label}"?`)) {
            return;
        }

        try {
            this.setState({ busyCustomRoleId: role.id });
            await this.props.onDeleteCustomRole(role.id);
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleDeleteCustomRole(): Error deleting role:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the custom role.');
        } finally {
            this.setState({ busyCustomRoleId: null });
        }
    }

    private renderRolesAndAccess() {
        // Provider availability is independent from the subscription's feature limits.
        const features = this.props.dashboardFeatures.filter((feature) => isDashboardFeatureAvailableForProvider({
            dashboardFeatureAvailability: this.props.dashboardFeatureAvailability,
        }, feature.id));
        return (
            <div style={styles.rolesAccessContainer}>
                <div style={styles.rolesAccessHeader}>
                    <div>
                        <h2 style={styles.sectionTitle}>Roles & access</h2>
                        <p style={styles.rolesAccessHint}>
                            Changes apply immediately. Managing the organisation team grants viewing and allows
                            creating, editing, and deleting team members. Root and Super always have every feature.
                        </p>
                    </div>
                    {this.props.canCreateCustomRoles && (
                        <Button
                            onClick={this.handleCreateCustomRole}
                            label={this.state.busyCustomRoleId === 'new' ? 'Creating...' : 'New custom role'}
                            icon={require('./assets/icons/add_selected.svg').default}
                            type='primary'
                            disabled={!!this.state.busyCustomRoleId || !!this.state.busyDashboardFeatureKey}
                        />
                    )}
                </div>
                <div style={styles.rolesAccessTableContainer}>
                    <table style={styles.accessTable}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.accessRoleHeaderCell}>Role</th>
                                {features.map((feature) => (
                                    <th key={feature.id} style={styles.accessFeatureHeaderCell}>{feature.label}</th>
                                ))}
                                <th style={styles.actionsHeaderCell}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.props.dashboardRoles.map((role) => {
                                const isCustomRole = this.props.dashboardCustomRoles.some((customRole) => customRole.id === role.id);
                                return (
                                    <tr key={role.id}>
                                        <td style={styles.accessRoleCell}>{role.label}</td>
                                        {features.map((feature) => {
                                            const busyKey = `${role.id}:${feature.id}`;
                                            const outsidePlan = !isDashboardFeatureIncludedInPlan(this.props, feature.id);
                                            const fixedTeamPermission = this.props.commercialModel === 'self_service' &&
                                                (feature.id === 'organisation_team_manage' ||
                                                    (role.id === 'admin' && feature.id === 'organisation_team'));
                                            return (
                                                <td key={feature.id} style={styles.accessFeatureCell}>
                                                    <input
                                                        type='checkbox'
                                                        checked={!!this.props.dashboardFeatureAccess[role.id]?.[feature.id]}
                                                        onChange={(event) => void this.handleDashboardFeatureChange(
                                                            role.id,
                                                            feature.id,
                                                            event.currentTarget.checked
                                                        )}
                                                        disabled={outsidePlan || fixedTeamPermission ||
                                                            !!this.state.busyDashboardFeatureKey || !!this.state.busyCustomRoleId}
                                                        aria-label={`${feature.label} for ${role.label}${
                                                            outsidePlan ? ' — upgrade required' : ''
                                                        }`}
                                                        title={outsidePlan ? 'Upgrade your plan to enable this feature.' :
                                                            fixedTeamPermission ? 'Team management is fixed by this SaaS role.' : undefined}
                                                    />
                                                    {outsidePlan && <span style={styles.accessLockedLabel}>Upgrade</span>}
                                                    {this.state.busyDashboardFeatureKey === busyKey && (
                                                        <span style={styles.accessSavingLabel}>Saving...</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td style={styles.actionsCell}>
                                            {isCustomRole && (
                                                <button
                                                    type='button'
                                                    onClick={() => void this.handleDeleteCustomRole(role)}
                                                    style={styles.customRoleDeleteButton}
                                                    disabled={!!this.state.busyCustomRoleId || !!this.state.busyDashboardFeatureKey}
                                                >
                                                    {this.state.busyCustomRoleId === role.id ? 'Deleting...' : 'Delete'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private isLegacyProvider(): boolean {
        return this.props.commercialModel !== 'self_service';
    }

    private canValidatePremiumMigration(): boolean {
        return this.props.dashboardRole === 'root' && this.isLegacyProvider();
    }

    private canAcceptPremiumMigrationOffer(): boolean {
        return this.props.dashboardRole === 'admin' && this.isLegacyProvider();
    }

    private getPremiumMigrationMinimumUnits(preview: ExistingProviderPremiumMigrationPreview): number {
        // Bind the picker directly to the retained team count as well as the server's overall capacity minimum.
        return Math.max(preview.minimumSubscriptionUnits, preview.registeredDashboardUserCount);
    }

    private loadPremiumMigrationData(): void {
        // Root gets an independent switch read so an enabled-but-stale offer can always be disabled.
        if (this.canValidatePremiumMigration()) {
            void this.loadPremiumMigrationOfferState();
        }
        if (this.canValidatePremiumMigration() || this.canAcceptPremiumMigrationOffer()) {
            void this.loadPremiumMigrationPreview();
        }
    }

    private async loadPremiumMigrationOfferState(force = false): Promise<void> {
        if (!this.canValidatePremiumMigration() || this.state.migrationOfferStateLoading ||
            (!force && this.state.migrationOfferState)) {
            return;
        }
        const healthcareProviderId = this.props.healthcareProviderId;
        const operationGeneration = ++this.migrationOfferOperationGeneration;
        this.setState({ migrationOfferStateLoading: true, migrationOfferError: null });
        try {
            const offerState = await this.props.onGetExistingProviderPremiumMigrationOfferState();
            if (operationGeneration !== this.migrationOfferOperationGeneration ||
                healthcareProviderId !== this.props.healthcareProviderId ||
                !this.canValidatePremiumMigration()) {
                return;
            }
            this.setState({
                migrationOfferState: offerState,
                migrationOfferStateLoading: false,
                migrationOfferError: null,
            });
        } catch (error) {
            if (operationGeneration !== this.migrationOfferOperationGeneration) return;
            this.setState({
                migrationOfferStateLoading: false,
                migrationOfferError: this.getMigrationErrorMessage(
                    error,
                    'The customer-offer setting could not be loaded. Please try again.'
                ),
            });
        }
    }

    private async loadPremiumMigrationPreview(force = false): Promise<void> {
        // Admins only receive this preview when Root has exposed a still-current offer.
        if ((!this.canValidatePremiumMigration() && !this.canAcceptPremiumMigrationOffer()) ||
            this.state.migrationPreviewLoading || (!force && this.state.migrationPreview)) {
            return;
        }

        const healthcareProviderId = this.props.healthcareProviderId;
        const operationGeneration = ++this.migrationOperationGeneration;
        // A fresh check must not leave an older approval available for Checkout.
        this.setState({ migrationPreview: null, migrationPreviewLoading: true, migrationError: null });
        try {
            const preview = await this.props.onGetExistingProviderPremiumMigrationPreview();
            if (operationGeneration !== this.migrationOperationGeneration ||
                healthcareProviderId !== this.props.healthcareProviderId ||
                (!this.canValidatePremiumMigration() && !this.canAcceptPremiumMigrationOffer())) {
                return;
            }
            this.setState({
                migrationPreview: preview,
                migrationPreviewLoading: false,
                migrationError: null,
                migrationBillingOwnerTherapistId: this.canAcceptPremiumMigrationOffer() && preview.mayCheckout ?
                    preview.currentAdminTherapistId || '' : '',
                migrationBillingInterval: 'monthly',
                migrationSubscriptionUnits: this.getPremiumMigrationMinimumUnits(preview),
                migrationCheckoutLoading: false,
            });
        } catch (error) {
            if (operationGeneration !== this.migrationOperationGeneration) return;
            this.setState({
                migrationPreview: null,
                migrationPreviewLoading: false,
                migrationBillingOwnerTherapistId: '',
                migrationError: this.canValidatePremiumMigration() ? this.getMigrationErrorMessage(
                    error,
                    'The Premium migration requirements could not be loaded. Please try again.'
                ) : this.getCustomerMigrationErrorMessage(
                    error,
                    'Subscription options could not be loaded. Please try again.'
                ),
            });
        }
    }

    private async handlePremiumMigrationOfferEnabled(enabled: boolean): Promise<void> {
        if (!this.canValidatePremiumMigration() || this.state.migrationOfferSettingLoading) return;
        const retryingPendingEnable = enabled && ['enabling', 'enable_pending'].includes(
            this.state.migrationOfferState?.offerStatus || ''
        );
        const validationToken = enabled && !retryingPendingEnable ?
            this.state.migrationPreview?.validationToken : undefined;
        if (enabled && !retryingPendingEnable && !validationToken) {
            this.setState({
                migrationOfferError: 'Validate the current organisation and Premium capacity before enabling checkout.',
            });
            return;
        }
        const healthcareProviderId = this.props.healthcareProviderId;
        const operationGeneration = ++this.migrationOfferOperationGeneration;
        this.setState({ migrationOfferSettingLoading: true, migrationOfferError: null });
        try {
            const offerState = enabled && validationToken ?
                await this.props.onSetExistingProviderPremiumMigrationOfferEnabled(true, validationToken) :
                await this.props.onSetExistingProviderPremiumMigrationOfferEnabled(enabled);
            if (operationGeneration !== this.migrationOfferOperationGeneration ||
                healthcareProviderId !== this.props.healthcareProviderId ||
                !this.canValidatePremiumMigration()) {
                return;
            }
            this.setState({
                migrationOfferState: offerState,
                migrationOfferSettingLoading: false,
                migrationOfferError: null,
            });
        } catch (error) {
            if (operationGeneration !== this.migrationOfferOperationGeneration) return;
            const errorMessage = this.getMigrationErrorMessage(
                error,
                enabled ? 'The customer offer could not be enabled.' : 'The customer offer could not be disabled.'
            );
            const requiresFreshReview = this.isMigrationValidationRefreshRequired(error);
            if (requiresFreshReview) {
                // Never let Root approve a state different from the exact validation result shown on screen.
                this.migrationOperationGeneration += 1;
                this.setState({
                    migrationPreview: null,
                    migrationPreviewLoading: false,
                    migrationError: errorMessage,
                    migrationSubscriptionUnits: 1,
                });
            }
            await this.refreshPremiumMigrationOfferStateAfterTransitionError(errorMessage, !requiresFreshReview);
        }
    }

    private isMigrationValidationRefreshRequired(error: unknown): boolean {
        const reason = typeof (error as { details?: { reason?: unknown } })?.details?.reason === 'string' ?
            (error as { details: { reason: string } }).details.reason : '';
        return ['migration_validation_changed', 'migration_validation_required',
            'migration_offer_requires_revalidation'].includes(reason);
    }

    private async refreshPremiumMigrationOfferStateAfterTransitionError(
        errorMessage: string,
        showOfferError = true
    ): Promise<void> {
        // A setter may persist a pending/payment state before Stripe uncertainty makes it throw.
        const healthcareProviderId = this.props.healthcareProviderId;
        const operationGeneration = ++this.migrationOfferOperationGeneration;
        this.setState({
            migrationOfferSettingLoading: false,
            migrationOfferStateLoading: true,
            migrationOfferError: showOfferError ? errorMessage : null,
        });
        try {
            const offerState = await this.props.onGetExistingProviderPremiumMigrationOfferState();
            if (operationGeneration !== this.migrationOfferOperationGeneration ||
                healthcareProviderId !== this.props.healthcareProviderId ||
                !this.canValidatePremiumMigration()) {
                return;
            }
            this.setState({
                migrationOfferState: offerState,
                migrationOfferStateLoading: false,
                migrationOfferError: showOfferError ? errorMessage : null,
            });
        } catch (refreshError) {
            if (operationGeneration !== this.migrationOfferOperationGeneration) return;
            this.setState({
                migrationOfferStateLoading: false,
                migrationOfferError: `${errorMessage} ${this.getMigrationErrorMessage(
                    refreshError,
                    'The latest migration state could not be refreshed.'
                )}`,
            });
        }
    }

    private async handlePremiumMigrationCheckout(): Promise<void> {
        const preview = this.state.migrationPreview;
        if (!this.canAcceptPremiumMigrationOffer() || !preview ||
            !preview.offerEnabled || !preview.offerCurrent || !preview.mayCheckout ||
            this.state.migrationCheckoutLoading) return;
        if (!this.state.migrationBillingOwnerTherapistId) {
            this.setState({ migrationError: 'Choose an active Admin to own the subscription.' });
            return;
        }
        const subscriptionUnits = this.state.migrationSubscriptionUnits;
        const minimumSubscriptionUnits = this.getPremiumMigrationMinimumUnits(preview);
        if (!Number.isInteger(subscriptionUnits) ||
            subscriptionUnits < minimumSubscriptionUnits ||
            subscriptionUnits > preview.maximumSelfServeUnits) {
            this.setState({
                migrationError: `Choose between ${minimumSubscriptionUnits} and ` +
                    `${preview.maximumSelfServeUnits} subscription units.`,
            });
            return;
        }

        const healthcareProviderId = this.props.healthcareProviderId;
        const operationGeneration = ++this.migrationOperationGeneration;
        this.setState({ migrationCheckoutLoading: true, migrationError: null });
        try {
            await this.props.onCreateExistingProviderPremiumCheckout({
                billingOwnerTherapistId: this.state.migrationBillingOwnerTherapistId,
                billingInterval: this.state.migrationBillingInterval,
                subscriptionUnits,
            });
        } catch (error) {
            if (operationGeneration !== this.migrationOperationGeneration ||
                healthcareProviderId !== this.props.healthcareProviderId) {
                return;
            }
            this.setState({
                // Recheck approval before another attempt, including when it changed during Checkout creation.
                migrationPreview: null,
                migrationBillingOwnerTherapistId: '',
                migrationCheckoutLoading: false,
                migrationError: this.getCustomerMigrationErrorMessage(
                    error,
                    'Secure checkout could not be opened. Please try again.'
                ),
            });
        }
    }

    private formatMigrationPrice(amount: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    }

    private getMigrationErrorMessage(error: unknown, fallback: string): string {
        const message = error instanceof Error ? error.message.trim() : '';
        const generic = /^(?:internal|unknown|functions\/(?:internal|unknown)|firebase: error \(functions\/(?:internal|unknown)\)\.?)$/i;
        return message && !generic.test(message) ? message : fallback;
    }

    private getCustomerMigrationErrorMessage(error: unknown, fallback: string): string | null {
        // Only an explicitly disabled offer is hidden; failed checks need an actionable customer status.
        const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
        if (reason === 'migration_offer_not_enabled') return null;
        if (['migration_offer_requires_revalidation', 'migration_offer_no_longer_current',
            'unverified_dashboard_users'].includes(typeof reason === 'string' ? reason : '')) {
            return 'SuperPenguin needs to review your organisation’s subscription setup before you can continue. ' +
                'Please contact us, then try again once the review is complete.';
        }
        if (reason === 'migration_checkout_in_progress') {
            return 'Another Admin has already started checkout for this organisation. ' +
                'Please ask them to finish before trying again.';
        }
        if (reason === 'migration_payment_pending' || reason === 'migration_payment_already_committed') {
            return 'Your payment is being confirmed. Please wait for confirmation before starting another checkout.';
        }
        if (reason === 'migration_requires_manual_recovery') {
            return 'SuperPenguin needs to help complete your subscription. Please contact us before trying checkout again.';
        }
        return fallback;
    }

    private renderPremiumMigrationOfferControl(
        preview: ExistingProviderPremiumMigrationPreview | null
    ): React.ReactNode {
        const offerState = this.state.migrationOfferState;
        const offerStatus = offerState?.offerStatus;
        const offerTransitionLocked = !!offerStatus && [
            'payment_pending', 'completed', 'manual_recovery', 'enable_pending', 'disable_pending',
            'enabling', 'disabling',
        ].includes(offerStatus);
        return (
            <div style={styles.migrationOfferControl}>
                <div>
                    <strong>Customer checkout access</strong>
                    <p style={styles.rolesAccessHint}>
                        Enable this only after reviewing the validation results on this page. An active Admin will
                        then choose the billing period and capacity and complete payment securely in Stripe.
                    </p>
                    {offerStatus === 'payment_pending' && (
                        <p role='status' style={styles.migrationOfferStaleStatus}>
                            Stripe Checkout has completed and payment or the in-place migration is still being
                            confirmed. Customer access cannot be changed until that finishes.
                        </p>
                    )}
                    {offerStatus === 'manual_recovery' && (
                        <p role='alert' style={styles.migrationOfferRecoveryStatus}>
                            Payment was committed, but this migration needs manual recovery. Contact engineering before
                            changing the provider or starting another Checkout.
                        </p>
                    )}
                    {offerStatus === 'completed' && (
                        <p role='status' style={styles.migrationOfferEnabledStatus}>
                            Migration completed. Refresh the organisation to load its Premium billing details.
                        </p>
                    )}
                    {['enable_pending', 'enabling'].includes(offerStatus || '') && (
                        <p role='status' style={styles.migrationOfferStaleStatus}>
                            Enabling customer checkout is still processing. Refresh this status before trying again.
                        </p>
                    )}
                    {['disable_pending', 'disabling'].includes(offerStatus || '') && (
                        <p role='status' style={styles.migrationOfferStaleStatus}>
                            Hiding customer checkout is still processing. Refresh this status before trying again.
                        </p>
                    )}
                    {!offerTransitionLocked && offerState?.offerEnabled && offerState.offerCurrent && (
                        <p role='status' style={styles.migrationOfferEnabledStatus}>
                            Enabled — the customer can choose a subscription and pay.
                        </p>
                    )}
                    {!offerTransitionLocked && offerState?.offerEnabled && !offerState.offerCurrent && (
                        <p role='status' style={styles.migrationOfferStaleStatus}>
                            Enabled, but paused because the validated organisation details changed. Revalidate before
                            the customer can pay.
                        </p>
                    )}
                    {!offerTransitionLocked && offerState && !offerState.offerEnabled && (
                        <p role='status' style={styles.rolesAccessHint}>Hidden from the customer.</p>
                    )}
                </div>
                {this.state.migrationOfferStateLoading && (
                    <span role='status' style={styles.rolesAccessHint}>Loading setting…</span>
                )}
                {offerState && !offerTransitionLocked && (
                    <label style={styles.migrationSwitchLabel}>
                        <input
                            type='checkbox'
                            role='switch'
                            aria-label='Expose Premium checkout to this customer'
                            checked={offerState.offerEnabled}
                            onChange={(event) => void this.handlePremiumMigrationOfferEnabled(
                                event.currentTarget.checked
                            )}
                            disabled={this.state.migrationOfferSettingLoading ||
                                (!offerState.offerEnabled && !preview)}
                        />
                        {this.state.migrationOfferSettingLoading ? 'Saving…' :
                            offerState.offerEnabled ? 'Customer access enabled' : 'Enable customer access'}
                    </label>
                )}
                {!offerTransitionLocked && offerState?.offerEnabled && !offerState.offerCurrent && preview && (
                    <Button
                        onClick={() => void this.handlePremiumMigrationOfferEnabled(true)}
                        label={this.state.migrationOfferSettingLoading ? 'Revalidating…' : 'Revalidate and enable'}
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {offerStatus === 'enable_pending' && (
                    <Button
                        onClick={() => void this.handlePremiumMigrationOfferEnabled(true)}
                        label={this.state.migrationOfferSettingLoading ? 'Retrying…' : 'Retry enabling customer access'}
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {offerStatus === 'disable_pending' && (
                    <Button
                        onClick={() => void this.handlePremiumMigrationOfferEnabled(false)}
                        label={this.state.migrationOfferSettingLoading ? 'Retrying…' : 'Retry hiding customer access'}
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {offerState && offerTransitionLocked && !this.state.migrationOfferStateLoading && (
                    <Button
                        onClick={() => void this.loadPremiumMigrationOfferState(true)}
                        label='Refresh migration status'
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {!this.state.migrationOfferStateLoading && !offerState && (
                    <Button
                        onClick={() => void this.loadPremiumMigrationOfferState(true)}
                        label='Retry offer setting'
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {this.state.migrationOfferError && (
                    <div role='alert' style={styles.billingOverageAlert}>{this.state.migrationOfferError}</div>
                )}
            </div>
        );
    }

    private renderPremiumMigrationPanel(): React.ReactNode {
        const isRootValidation = this.canValidatePremiumMigration();
        const isCustomerOffer = this.canAcceptPremiumMigrationOffer();
        const hasCurrentCustomerPreview = !!this.state.migrationPreview?.offerEnabled &&
            !!this.state.migrationPreview.offerCurrent && !!this.state.migrationPreview.mayCheckout;
        // Customer status can render without approval, but subscription controls always require a current offer.
        if (!isRootValidation && (!isCustomerOffer || (!hasCurrentCustomerPreview &&
            !this.state.migrationPreviewLoading && !this.state.migrationError))) return null;
        const preview = isRootValidation || hasCurrentCustomerPreview ? this.state.migrationPreview : null;
        const units = this.state.migrationSubscriptionUnits;
        const minimumSubscriptionUnits = preview ? this.getPremiumMigrationMinimumUnits(preview) : 1;
        const unitPrice = this.state.migrationBillingInterval === 'monthly' ?
            preview?.monthlyUnitPrice : preview?.annualUnitPrice;
        const intervalLabel = this.state.migrationBillingInterval === 'monthly' ? 'month' : 'year';
        const currentAdmin = preview?.activeAdmins.find(
            (admin) => admin.therapistId === preview.currentAdminTherapistId
        );

        return (
            <section style={styles.migrationSection} aria-labelledby='premium-migration-heading'>
                <div>
                    <h3 id='premium-migration-heading' style={styles.billingOwnerHeading}>
                        {isRootValidation ?
                            'Move this organisation to Premium self-service' :
                            'Move your organisation to Premium self-service'}
                    </h3>
                    <p style={styles.rolesAccessHint}>
                        The existing provider, team, clients, files and activation keys stay in place. Its current
                        agreement remains unchanged unless Stripe confirms a successful payment.
                    </p>
                </div>

                {this.state.migrationPreviewLoading && (
                    <p role='status' style={styles.rolesAccessHint}>{isRootValidation ?
                        'Checking migration requirements…' : 'Loading subscription options…'}</p>
                )}
                {this.state.migrationError && (
                    <div role='alert' style={styles.billingOverageAlert}>
                        {this.state.migrationError}
                        {isRootValidation && this.state.migrationError.includes('verified sign-in identity') && (
                            <p style={styles.fieldHint}>
                                Open Organisation → Team and choose Resend verification email for each unverified
                                password user. After they verify, retry this migration check.
                            </p>
                        )}
                    </div>
                )}
                {!this.state.migrationPreviewLoading && !preview && (isRootValidation || this.state.migrationError) && (
                    <Button
                        onClick={() => void this.loadPremiumMigrationPreview(true)}
                        label={this.state.migrationError ? 'Try again' : 'Check eligibility'}
                        type='secondary'
                        disabled={this.state.migrationOfferSettingLoading}
                    />
                )}
                {isRootValidation && !preview && this.renderPremiumMigrationOfferControl(preview)}

                {preview && (
                    <>
                        <div style={styles.usageGrid}>
                            <div style={styles.usageCard}>
                                <span style={styles.usageLabel}>Existing dashboard users</span>
                                <strong>{preview.registeredDashboardUserCount}</strong>
                            </div>
                            <div style={styles.usageCard}>
                                <span style={styles.usageLabel}>Existing families using the app</span>
                                <strong>{preview.existingFamilies}</strong>
                                <span>Counted against the first licence year</span>
                                <span>Referrals without recorded mobile-app use are excluded</span>
                            </div>
                            <div style={styles.usageCard}>
                                <span style={styles.usageLabel}>Minimum Premium capacity</span>
                                <strong>{minimumSubscriptionUnits} unit
                                    {minimumSubscriptionUnits === 1 ? '' : 's'}</strong>
                            </div>
                            {isRootValidation && (
                                <div style={styles.usageCard}>
                                    <span style={styles.usageLabel}>Eligible billing Admins</span>
                                    <strong>{preview.activeAdmins.length}</strong>
                                    <span>{preview.activeAdmins.map((admin) => admin.email).join(', ')}</span>
                                </div>
                            )}
                        </div>
                        {isRootValidation && this.renderPremiumMigrationOfferControl(preview)}
                        {isCustomerOffer && <div style={styles.migrationFormGrid}>
                            <div style={styles.migrationField}>
                                <span style={styles.label}>Billing owner</span>
                                <strong>{currentAdmin ? `${currentAdmin.name} · ${currentAdmin.email}` :
                                    'Your Admin account is not eligible to own billing.'}</strong>
                                <span style={styles.rolesAccessHint}>
                                    The Admin completing payment becomes the subscription owner.
                                </span>
                            </div>

                            <fieldset style={styles.migrationFieldset} disabled={this.state.migrationCheckoutLoading}>
                                <legend style={styles.label}>Billing</legend>
                                <label style={styles.migrationRadioLabel}>
                                    <input
                                        type='radio'
                                        name='migration-billing-interval'
                                        value='monthly'
                                        checked={this.state.migrationBillingInterval === 'monthly'}
                                        onChange={() => this.setState({ migrationBillingInterval: 'monthly' })}
                                    />
                                    Monthly · {this.formatMigrationPrice(preview.monthlyUnitPrice)} per unit
                                </label>
                                <label style={styles.migrationRadioLabel}>
                                    <input
                                        type='radio'
                                        name='migration-billing-interval'
                                        value='annual'
                                        checked={this.state.migrationBillingInterval === 'annual'}
                                        onChange={() => this.setState({ migrationBillingInterval: 'annual' })}
                                    />
                                    Annually · {this.formatMigrationPrice(preview.annualUnitPrice)} per unit
                                </label>
                            </fieldset>

                            <div style={styles.migrationField}>
                                <label htmlFor='migration-subscription-units' style={styles.label}>Subscription units</label>
                                <input
                                    id='migration-subscription-units'
                                    type='number'
                                    min={minimumSubscriptionUnits}
                                    max={preview.maximumSelfServeUnits}
                                    step={1}
                                    value={units}
                                    onChange={(event) => this.setState({
                                        migrationSubscriptionUnits: Math.max(
                                            minimumSubscriptionUnits,
                                            Math.min(
                                                preview.maximumSelfServeUnits,
                                                Number(event.currentTarget.value) || minimumSubscriptionUnits
                                            )
                                        ),
                                        migrationError: null,
                                    })}
                                    style={styles.billingOwnerSelect}
                                    disabled={this.state.migrationCheckoutLoading}
                                />
                                <span style={styles.rolesAccessHint}>
                                    At least one unit per registered dashboard therapist. This organisation currently
                                    has {preview.registeredDashboardUserCount}.
                                </span>
                                <span style={styles.rolesAccessHint}>
                                    {units * preview.dashboardSeatsPerUnit} dashboard seat
                                    {units * preview.dashboardSeatsPerUnit === 1 ? '' : 's'} ·{' '}
                                    {units * preview.familyLicensesPerUnit} family licences/year
                                </span>
                            </div>
                        </div>}

                        {isCustomerOffer && <div style={styles.migrationCheckoutSummary}>
                            <div>
                                <span style={styles.usageLabel}>Premium subscription</span>
                                <strong style={styles.migrationPrice}>
                                    {this.formatMigrationPrice((unitPrice || 0) * units)}/{intervalLabel}
                                </strong>
                                <span style={styles.rolesAccessHint}>Excluding VAT</span>
                            </div>
                            <Button
                                onClick={() => void this.handlePremiumMigrationCheckout()}
                                label={this.state.migrationCheckoutLoading ?
                                    'Opening secure checkout…' : 'Continue to secure checkout'}
                                type='primary'
                                disabled={this.state.migrationCheckoutLoading ||
                                    !currentAdmin || !this.state.migrationBillingOwnerTherapistId}
                            />
                        </div>}
                    </>
                )}
            </section>
        );
    }

    private renderPlanAndBilling(): React.ReactNode {
        if (this.props.commercialModel !== 'self_service') {
            return (
                <div style={styles.billingContainer}>
                    <h2 style={styles.sectionTitle}>Custom agreement</h2>
                    <p style={styles.rolesAccessHint}>
                        This organisation is managed through its existing SuperPenguin agreement. SaaS limits and
                        Stripe billing do not apply.
                    </p>
                    {this.renderPremiumMigrationPanel()}
                </div>
            );
        }

        const usage = this.props.licenseUsage;
        const planName = this.props.entitlement?.planName ||
            (this.props.planCode === 'starter' ? 'Free trial' : 'Premium');
        const overage = usage ? getRequiredAdditionalLicenses(usage) : 0;
        const billingDetails = this.props.entitlement ?
            buildProviderBillingDetails(this.props.entitlement, usage?.periodEndsAt) : [];
        const billingOwnerCandidates = getEligibleBillingOwnerCandidates(this.props.therapists);
        const currentSubscriptionUnits = usage?.subscriptionUnits || 1;
        const minimumRequiredSubscriptionUnits = Math.min(
            currentSubscriptionUnits,
            Math.max(1, usage?.minimumRequiredSubscriptionUnits || 1)
        );
        const requestedCapacityIsIncrease = this.state.requestedSubscriptionUnits > currentSubscriptionUnits;
        const requestedCapacityIsDecrease = this.state.requestedSubscriptionUnits < currentSubscriptionUnits;

        return (
            <div style={styles.billingContainer}>
                <div style={styles.billingHeader}>
                    <div>
                        <h2 style={styles.sectionTitle}>{planName}</h2>
                        <p style={styles.rolesAccessHint}>
                            Subscription, payment and annual family allowance details for this organisation.
                        </p>
                    </div>
                    {this.props.canManageBilling && (
                        <div style={styles.billingActions}>
                            <Button
                                onClick={() => window.location.assign('/plans')}
                                label={this.props.planCode === 'starter' ? 'Upgrade plan' : 'Compare plans'}
                                type={this.props.planCode === 'starter' ? 'primary' : 'secondary'}
                                disabled={!!this.state.billingActionId}
                            />
                            {this.props.planCode !== 'starter' && (
                                <Button
                                    onClick={() => void this.runBillingAction('portal', this.props.onOpenBillingPortal)}
                                    label={this.state.billingActionId === 'portal' ? 'Opening...' : 'Manage payments'}
                                    type='secondary'
                                    disabled={!!this.state.billingActionId}
                                />
                            )}
                        </div>
                    )}
                </div>
                {billingDetails.length > 0 && (
                    <section aria-labelledby='subscription-details-heading' style={styles.billingSummarySection}>
                        <h3 id='subscription-details-heading' style={styles.billingOwnerHeading}>Subscription details</h3>
                        <dl style={styles.billingSummaryGrid}>
                            {billingDetails.map((detail) => (
                                <div key={detail.id} style={styles.billingSummaryItem}>
                                    <dt style={styles.usageLabel}>{detail.label}</dt>
                                    <dd style={styles.billingSummaryValue}>{detail.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}
                {/* The summary gates private Stripe requests by the effective Root/Super role. */}
                <ProviderPaymentSummary
                    healthcareProviderId={this.props.healthcareProviderId}
                    userId={this.props.userId}
                    dashboardRole={this.props.dashboardRole}
                />
                {usage && (
                    <div style={styles.usageGrid}>
                        <div style={styles.usageCard}>
                            <span style={styles.usageLabel}>Subscription units</span>
                            <strong>{usage.subscriptionUnits || 1}</strong>
                            <span>1 seat + {this.props.planCode === 'premium' ? 50 : 5} family licences per unit</span>
                        </div>
                        <div style={styles.usageCard}>
                            <span style={styles.usageLabel}>Dashboard seats</span>
                            <strong>{usage.dashboardSeatsUsed || 0} / {usage.dashboardSeatLimit || 0}</strong>
                            {!!usage.dashboardSeatsReserved && <span>{usage.dashboardSeatsReserved} reserved</span>}
                        </div>
                        <div style={overage > 0 ? styles.usageCardOverage : styles.usageCard}>
                            <span style={styles.usageLabel}>Family licenses available</span>
                            <strong>{usage.availableLicenses}</strong>
                            {usage.licenseModel === 'annual_activation' && (
                                <span>
                                    {usage.numberOfLicensesAvailablePerYear ?? usage.includedLicenses} available per year
                                </span>
                            )}
                            <span>{usage.usedLicensesSinceAnnualRenewalDate} used this licence year</span>
                        </div>
                        <div style={styles.usageCard}>
                            <span style={styles.usageLabel}>Families using the app</span>
                            <strong>{usage.currentlySponsoredFamilies}</strong>
                            <span>Sponsored families with recorded app use by a linked carer</span>
                        </div>
                        {usage.audioScribeRecordingsLimit !== null && usage.audioScribeRecordingsLimit !== undefined && (
                            <div style={styles.usageCard}>
                                <span style={styles.usageLabel}>Audio Scribe recordings</span>
                                <strong>{usage.audioScribeRecordingsUsed || 0} / {usage.audioScribeRecordingsLimit}</strong>
                                <span>{usage.audioScribeRecordingsRemaining || 0} remaining on Free trial</span>
                            </div>
                        )}
                        {usage.aiGeneratedReportsLimit !== null && usage.aiGeneratedReportsLimit !== undefined && (
                            <div style={styles.usageCard}>
                                <span style={styles.usageLabel}>AI-generated reports</span>
                                <strong>{usage.aiGeneratedReportsUsed || 0} / {usage.aiGeneratedReportsLimit}</strong>
                                <span>{usage.aiGeneratedReportsRemaining || 0} remaining on Free trial</span>
                            </div>
                        )}
                    </div>
                )}
                {overage > 0 && (
                    <div role='alert' style={styles.billingOverageAlert}>
                        This organisation is {overage} family licence{overage === 1 ? '' : 's'} over its annual allowance.
                        Increase Premium capacity within 30 days to keep dashboard service active. Existing families keep
                        access to SuperPenguin.
                    </div>
                )}
                {this.props.canManageBilling && this.props.planCode === 'premium' && usage && (
                    <section style={styles.billingOwnerSection} aria-labelledby='premium-capacity-heading'>
                        <div>
                            <h3 id='premium-capacity-heading' style={styles.billingOwnerHeading}>Change Premium capacity</h3>
                            <p style={styles.rolesAccessHint}>
                                Each unit provides one dashboard seat and 50 family licences per year. You may lower capacity only
                                while the remaining units still cover occupied seats and family licences already used this year.
                            </p>
                            <p style={styles.rolesAccessHint}>
                                This organisation currently needs at least {minimumRequiredSubscriptionUnits} unit
                                {minimumRequiredSubscriptionUnits === 1 ? '' : 's'}.
                            </p>
                        </div>
                        <div style={styles.billingOwnerControls}>
                            <label htmlFor='premium-subscription-units' style={styles.usageLabel}>Subscription units</label>
                            <input
                                id='premium-subscription-units'
                                type='number'
                                min={minimumRequiredSubscriptionUnits}
                                max={20}
                                step={1}
                                value={this.state.requestedSubscriptionUnits}
                                onChange={(event) => this.setState({
                                    requestedSubscriptionUnits: Math.max(
                                        minimumRequiredSubscriptionUnits,
                                        Math.min(20, Number(event.target.value) || minimumRequiredSubscriptionUnits)
                                    ),
                                })}
                                style={styles.billingOwnerSelect}
                                disabled={!!this.state.billingActionId}
                            />
                            <span style={styles.rolesAccessHint}>
                                {this.state.requestedSubscriptionUnits} seat{this.state.requestedSubscriptionUnits === 1 ? '' : 's'} ·{' '}
                                {this.state.requestedSubscriptionUnits * 50} family licences/year
                            </span>
                            <Button
                                onClick={() => void this.handleSubscriptionUnitChange()}
                                label={this.state.billingActionId === 'capacity' ? 'Updating…' :
                                    requestedCapacityIsIncrease ? 'Increase capacity' :
                                        requestedCapacityIsDecrease ? 'Lower capacity' : 'Update capacity'}
                                type='primary'
                                disabled={!!this.state.billingActionId ||
                                    this.state.requestedSubscriptionUnits === currentSubscriptionUnits}
                            />
                            {requestedCapacityIsIncrease && (
                                <span style={styles.rolesAccessHint}>Stripe invoices the prorated increase immediately.</span>
                            )}
                            {requestedCapacityIsDecrease && (
                                <span style={styles.rolesAccessHint}>
                                    Stripe applies the prorated downgrade credit to the next invoice.
                                </span>
                            )}
                            {this.state.requestedSubscriptionUnits === 20 && (
                                <a href='mailto:support@superpenguin.com?subject=Enterprise%20SuperPenguin%20capacity'>Need more? Talk to us</a>
                            )}
                        </div>
                    </section>
                )}
                {this.props.canManageBilling && (
                    <section style={styles.billingOwnerSection} aria-labelledby='billing-owner-heading'>
                        <div>
                            <h3 id='billing-owner-heading' style={styles.billingOwnerHeading}>Billing owner</h3>
                            <p style={styles.rolesAccessHint}>
                                Transfer Stripe billing and license-purchase authority to another active Admin.
                            </p>
                        </div>
                        {billingOwnerCandidates.length > 0 ? (
                            <div style={styles.billingOwnerControls}>
                                <label htmlFor='billing-owner-select' style={styles.usageLabel}>New billing owner</label>
                                <select
                                    id='billing-owner-select'
                                    value={this.state.selectedBillingOwnerTherapistId}
                                    onChange={(event) => this.setState({
                                        selectedBillingOwnerTherapistId: event.target.value,
                                    })}
                                    style={styles.billingOwnerSelect}
                                    disabled={!!this.state.billingActionId}
                                >
                                    <option value=''>Select an Admin</option>
                                    {billingOwnerCandidates.map((therapist) => (
                                        <option key={therapist.id} value={therapist.id}>{therapist.name}</option>
                                    ))}
                                </select>
                                <Button
                                    onClick={() => void this.handleTransferBillingOwnership()}
                                    label={this.state.billingActionId === 'transfer-owner' ? 'Transferring...' : 'Transfer ownership'}
                                    type='secondary'
                                    disabled={!this.state.selectedBillingOwnerTherapistId || !!this.state.billingActionId}
                                />
                            </div>
                        ) : (
                            <span style={styles.rolesAccessHint}>Create or activate another Admin before transferring ownership.</span>
                        )}
                    </section>
                )}
            </div>
        );
    }

    private async handleSubscriptionUnitChange(): Promise<void> {
        const currentUnits = this.props.licenseUsage?.subscriptionUnits || 1;
        const requestedUnits = this.state.requestedSubscriptionUnits;
        if (requestedUnits === currentUnits) return;
        await this.runBillingAction('capacity', async () => {
            await this.props.onChangeSubscriptionUnits(requestedUnits);
            window.location.reload();
        });
    }

    private async runBillingAction(actionId: string, action: () => Promise<void>): Promise<void> {
        try {
            this.setState({ billingActionId: actionId });
            await action();
        } catch (error) {
            console.error('ORGANISATION_VIEW: Billing action failed:', error);
            window.alert(error instanceof Error ? error.message : 'The billing action could not be completed.');
            this.setState({ billingActionId: null });
        }
    }

    private async handleTransferBillingOwnership(): Promise<void> {
        const therapistId = this.state.selectedBillingOwnerTherapistId;
        const therapist = this.props.therapists.find((candidate) => candidate.id === therapistId);
        if (!therapist || !window.confirm(
            `Transfer billing ownership to ${therapist.name}? You may lose access to billing after this change.`
        )) {
            return;
        }

        try {
            this.setState({ billingActionId: 'transfer-owner' });
            await this.props.onTransferBillingOwnership(therapistId);
            this.setState({ billingActionId: null, selectedBillingOwnerTherapistId: '' });
        } catch (error) {
            console.error('ORGANISATION_VIEW: Billing ownership transfer failed:', error);
            window.alert(error instanceof Error ? error.message : 'Billing ownership could not be transferred.');
            this.setState({ billingActionId: null });
        }
    }

    private showNewReportTemplateDialog = () => {
        // Start a fresh template form with empty optional instructions.
        this.setState({
            showReportTemplateDialog: true,
            reportTemplateName: '',
            reportTemplateFile: null,
            reportTemplatePromptExtraInstructions: '',
            editingReportTemplate: null,
            isCreatingReportTemplate: false,
        });
    }

    private showEditReportTemplateDialog = (reportTemplate: ReportTemplate) => {
        // Reuse the template form for metadata edits and optional file replacement.
        this.setState({
            showReportTemplateDialog: true,
            reportTemplateName: reportTemplate.name,
            reportTemplateFile: null,
            reportTemplatePromptExtraInstructions: reportTemplate.promptExtraInstructions || '',
            editingReportTemplate: reportTemplate,
            isCreatingReportTemplate: false,
        });
    }

    private hideReportTemplateDialog = () => {
        this.setState({
            showReportTemplateDialog: false,
            reportTemplateName: '',
            reportTemplateFile: null,
            reportTemplatePromptExtraInstructions: '',
            editingReportTemplate: null,
            isCreatingReportTemplate: false,
        });
    }

    private handleSaveReportTemplate = async () => {
        // Submit the template form as either a new upload or an edit to an existing row.
        const {
            reportTemplateName,
            reportTemplateFile,
            reportTemplatePromptExtraInstructions,
            editingReportTemplate,
        } = this.state;

        try {
            this.setState({ isCreatingReportTemplate: true });
            if (editingReportTemplate) {
                await this.props.onUpdateReportTemplate(
                    editingReportTemplate,
                    reportTemplateName,
                    reportTemplateFile,
                    reportTemplatePromptExtraInstructions
                );
            } else {
                await this.props.onCreateReportTemplate(
                    reportTemplateName,
                    reportTemplateFile,
                    reportTemplatePromptExtraInstructions
                );
            }
            this.hideReportTemplateDialog();
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleSaveReportTemplate(): Error saving template:', error);
            window.alert(error instanceof Error ? error.message : 'Could not save the report template.');
            this.setState({ isCreatingReportTemplate: false });
        }
    }

    private handleDeleteReportTemplate = async (reportTemplate: ReportTemplate) => {
        // Confirm before removing a provider report template and its uploaded file.
        const confirmed = window.confirm(`Delete report template "${reportTemplate.name}"?`);
        if (!confirmed) {
            return;
        }

        try {
            this.setState({ busyReportTemplateId: reportTemplate.id });
            await this.props.onDeleteReportTemplate(reportTemplate);
            this.setState({ busyReportTemplateId: null, hoveredReportTemplateId: null });
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleDeleteReportTemplate(): Error deleting template:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the report template.');
            this.setState({ busyReportTemplateId: null });
        }
    }

    private handleDownloadReportTemplate = async (reportTemplate: ReportTemplate) => {
        // Download the uploaded template file from its row menu.
        try {
            this.setState({ busyReportTemplateId: reportTemplate.id });
            await this.props.onDownloadReportTemplate(reportTemplate);
            this.setState({ busyReportTemplateId: null });
        } catch (error) {
            console.error('ORGANISATION_VIEW: handleDownloadReportTemplate(): Error downloading template:', error);
            window.alert(error instanceof Error ? error.message : 'Could not download the report template.');
            this.setState({ busyReportTemplateId: null });
        }
    }

    private renderReportTemplates() {
        const { reportTemplates } = this.props;
        const isEditingReportTemplate = !!this.state.editingReportTemplate;
        const isSaveReportTemplateDisabled = this.state.isCreatingReportTemplate ||
            !this.state.reportTemplateName.trim();

        return (
            <div style={styles.reportTemplatesContainer}>
                <div style={styles.actionsContainer}>
                    <Button
                        onClick={this.showNewReportTemplateDialog}
                        label='New'
                        icon={require('./assets/icons/add_selected.svg').default}
                        type='primary'
                    />
                </div>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.headerCell}>Name</th>
                                <th style={styles.reportTemplateFileHeaderCell}>File</th>
                                <th style={styles.actionsHeaderCell}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportTemplates.length === 0 ? (
                                <tr>
                                    <td style={styles.emptyTableCell} colSpan={3}>No report templates uploaded yet.</td>
                                </tr>
                            ) : (
                                reportTemplates.map((reportTemplate) => {
                                    const isHovered = this.state.hoveredReportTemplateId === reportTemplate.id;
                                    const isBusy = this.state.busyReportTemplateId === reportTemplate.id;

                                    return (
                                        <tr
                                            key={reportTemplate.id}
                                            style={{
                                                backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                            }}
                                            onMouseEnter={() => this.setState({ hoveredReportTemplateId: reportTemplate.id })}
                                            onMouseLeave={() => {
                                                if (this.state.hoveredReportTemplateId === reportTemplate.id) {
                                                    this.setState({ hoveredReportTemplateId: null });
                                                }
                                            }}
                                        >
                                            <td style={styles.tableCell}>{reportTemplate.name}</td>
                                            <td style={styles.tableCell}>
                                                {reportTemplate.originalName || 'No document'}
                                            </td>
                                            <td style={styles.actionsCell}>
                                                <TableRowEllipsisMenuButton
                                                    ariaLabel={`Options for ${reportTemplate.name}`}
                                                    isVisible={isHovered}
                                                    options={[{
                                                        label: 'Edit',
                                                        disabled: isBusy,
                                                        onClick: () => this.showEditReportTemplateDialog(reportTemplate),
                                                    }, {
                                                        label: 'Download',
                                                        disabled: isBusy || !reportTemplate.storagePath,
                                                        onClick: () => this.handleDownloadReportTemplate(reportTemplate),
                                                    }, {
                                                        label: 'Delete',
                                                        destructive: true,
                                                        disabled: isBusy,
                                                        onClick: () => this.handleDeleteReportTemplate(reportTemplate),
                                                    }]}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                {this.state.showReportTemplateDialog && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>
                                {isEditingReportTemplate ? 'Edit report template' : 'New report template'}
                            </h3>
                            <div style={styles.formField}>
                                <label style={styles.label}>Name</label>
                                <input
                                    type='text'
                                    value={this.state.reportTemplateName}
                                    onChange={(event) => this.setState({ reportTemplateName: event.target.value })}
                                    placeholder='Template name'
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.label}>Special instructions</label>
                                <textarea
                                    value={this.state.reportTemplatePromptExtraInstructions}
                                    onChange={(event) => this.setState({
                                        reportTemplatePromptExtraInstructions: event.target.value,
                                    })}
                                    placeholder='Optional prompt instructions for this template'
                                    style={styles.textarea}
                                    rows={5}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.label}>Document file</label>
                                <input
                                    type='file'
                                    onChange={(event) => this.setState({ reportTemplateFile: event.currentTarget.files?.[0] || null })}
                                    style={styles.fileInput}
                                />
                                <div style={styles.fileHint}>
                                    {isEditingReportTemplate ?
                                        (this.state.editingReportTemplate?.originalName ?
                                            `Leave blank to keep ${this.state.editingReportTemplate.originalName}.` :
                                            'Leave blank to keep this template without a document.') :
                                        'Optional. Leave blank to create a template without a document.'}
                                </div>
                                <div style={styles.fileHint}>
                                    PDF files are preferred and usually give the model the most effective template reference.
                                </div>
                            </div>
                            <div style={styles.dialogButtons}>
                                <Button
                                    onClick={this.hideReportTemplateDialog}
                                    label='Cancel'
                                    type='secondary'
                                    disabled={this.state.isCreatingReportTemplate}
                                />
                                <Button
                                    onClick={this.handleSaveReportTemplate}
                                    label={this.state.isCreatingReportTemplate ?
                                        (isEditingReportTemplate ? 'Saving...' : 'Creating...') :
                                        (isEditingReportTemplate ? 'Save' : 'Create')}
                                    type='primary'
                                    disabled={isSaveReportTemplateDisabled}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        // Replace the organisation tabs with this member's Timeline-only page.
        const selectedMember = this.props.canViewTeam &&
            this.state.selectedTeamMemberProviderId === this.props.healthcareProviderId ?
            this.props.therapists.find((member) => member.id === this.state.selectedTeamMemberId &&
                member.healthcareProviderId === this.props.healthcareProviderId) : undefined;
        if (selectedMember && this.props.teamTimeline) {
            return (
                <TeamMemberTimelineView
                    key={`${this.props.healthcareProviderId}:${selectedMember.id}`}
                    member={selectedMember}
                    teamTimeline={this.props.teamTimeline}
                    onBack={() => this.setState({
                        selectedTeamMemberId: null,
                        selectedTeamMemberProviderId: null,
                        selectedTabId: 'team',
                    })}
                />
            );
        }

        // Fall back to the first tab when the selected one is unavailable, e.g. while
        // provider info is still loading or after switching to a provider without it.
        const tabs = this.getTabs();
        const selectedTabsItemIndex = Math.max(0, tabs.findIndex(tab => tab.id === this.state.selectedTabId));

        return (
            <div style={styles.container}>
                {/* TabsView only reads the selected index at mount, so remount it whenever the tab set changes. */}
                <TabsView
                    key={tabs.map(tab => tab.id).join('|')}
                    options={tabs}
                    selectedTabsItemIndex={selectedTabsItemIndex}
                />
                {this.renderContent(tabs[selectedTabsItemIndex])}
            </div>
        );
    }
}

const styles = {
    // Keep the selected reply address visible alongside its Team member.
    messagingReplyToDetails: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '6px',
        marginTop: '6px',
        fontSize: '12px',
        overflowWrap: 'anywhere' as const,
    },
    teamMemberNameButton: {
        appearance: 'none' as const,
        padding: 0,
        margin: 0,
        border: 0,
        background: 'transparent',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',
    },
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as 'column',
        flexGrow: 1,
        backgroundColor: Theme.Colours.background,
    },
    dashboardNotSetUp: {
        flex: 1,
        margin: '28px 32px 0px 32px', // top right bottom left
    },
    // Login-locations map filling the whole tab, like the Amplitude panel.
    heatmap: {
        flex: 1,
        margin: '28px 32px 0px 32px', // top right bottom left
        overflow: 'hidden',
        borderRadius: '10px',
        border: Theme.Colours.border,
    },
    amplitudeDashboard: {
        flex: 1,
        margin: '28px 32px 0px 32px', // top right bottom left
        overflow: 'hidden',
        borderRadius: '10px',
        border: Theme.Colours.border,
    },
    iframe: {
        border: 'none',
        outline: 'none',
        position: 'relative' as 'relative',
        left: '-1px',
        top: '-1px',
        width: 'calc(100% + 2px)',
        height: 'calc(100% + 2px)',
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 20,
    },
    profileContainer: {
        width: 'calc(100% - 72px)',
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '24px',
        flex: 1,
        alignItems: 'flex-start',
        overflowY: 'auto' as const,
    },
    profileCard: {
        width: '100%',
        maxWidth: '720px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '28px',
        padding: '28px',
        boxSizing: 'border-box' as const,
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '12px',
        flexShrink: 0,
    },
    featureAvailabilityList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '18px',
    },
    featureAvailabilityRow: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    profileField: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
    },
    logoEditorRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        flexWrap: 'wrap' as const,
    },
    logoPreview: {
        width: '160px',
        height: '84px',
        objectFit: 'contain' as const,
        padding: '10px',
        boxSizing: 'border-box' as const,
        border: Theme.Colours.border,
        borderRadius: '10px',
        backgroundColor: Theme.Colours.background,
    },
    logoNameFallback: {
        ...Theme.Fonts.body,
        width: '160px',
        minHeight: '84px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
        boxSizing: 'border-box' as const,
        border: Theme.Colours.border,
        borderRadius: '10px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        fontWeight: 700,
        textAlign: 'center' as const,
        overflowWrap: 'anywhere' as const,
    },
    logoControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap' as const,
    },
    logoUploadLabel: {
        ...Theme.Fonts.body,
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: '40px',
        padding: '0 16px',
        border: Theme.Colours.border,
        borderRadius: '8px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        fontWeight: 600,
        cursor: 'pointer',
    },
    visuallyHiddenFileInput: {
        position: 'absolute' as const,
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap' as const,
        border: 0,
    },
    removeLogoButton: {
        ...Theme.Fonts.body,
        border: 'none',
        background: 'transparent',
        color: '#b42318',
        padding: '8px 0',
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    logoSelection: {
        ...Theme.Fonts.body,
        margin: 0,
        color: Theme.Colours.text,
        fontWeight: 600,
        overflowWrap: 'anywhere' as const,
    },
    profileError: {
        ...Theme.Fonts.body,
        padding: '12px 14px',
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '8px',
    },
    profileActions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    teamContainer: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden' as const,
    },
    reportTemplatesContainer: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden' as const,
    },
    rolesAccessContainer: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden' as const,
    },
    rolesAccessHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '24px',
        marginBottom: '20px',
        flexShrink: 0,
    },
    sectionTitle: {
        ...Theme.Fonts.h2,
        margin: 0,
        color: Theme.Colours.text,
    },
    rolesAccessHint: {
        ...Theme.Fonts.body,
        margin: '8px 0 0 0',
        color: Theme.Colours.text,
        opacity: 0.72,
    },
    rolesAccessTableContainer: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto' as const,
        paddingBottom: '20px',
    },
    accessTable: {
        minWidth: '900px',
        width: '100%',
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
        borderSpacing: 0,
    },
    accessRoleHeaderCell: {
        ...Theme.Fonts.body,
        minWidth: '190px',
        padding: '16px 20px',
        textAlign: 'left' as const,
        fontWeight: 'bold' as const,
        borderBottom: Theme.Colours.border,
    },
    accessFeatureHeaderCell: {
        ...Theme.Fonts.body,
        minWidth: '125px',
        padding: '16px 12px',
        textAlign: 'center' as const,
        verticalAlign: 'bottom' as const,
        fontWeight: 'bold' as const,
        borderBottom: Theme.Colours.border,
    },
    accessRoleCell: {
        ...Theme.Fonts.body,
        padding: '16px 20px',
        fontWeight: 600,
        borderBottom: Theme.Colours.border,
        whiteSpace: 'nowrap' as const,
    },
    accessFeatureCell: {
        ...Theme.Fonts.body,
        padding: '16px 12px',
        textAlign: 'center' as const,
        borderBottom: Theme.Colours.border,
        whiteSpace: 'nowrap' as const,
    },
    accessSavingLabel: {
        marginLeft: '6px',
        fontSize: '11px',
        opacity: 0.65,
    },
    accessLockedLabel: {
        display: 'block',
        marginTop: '4px',
        fontSize: '10px',
        color: '#6f5ac7',
        fontWeight: 600,
    },
    billingContainer: {
        width: 'calc(100% - 72px)',
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '24px',
        overflowY: 'auto' as const,
    },
    billingHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '20px',
    },
    billingActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap' as const,
    },
    billingSummarySection: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        padding: '18px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    billingSummaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px 24px',
        margin: 0,
    },
    billingSummaryItem: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '5px',
        minWidth: 0,
    },
    billingSummaryValue: {
        ...Theme.Fonts.body,
        margin: 0,
        color: Theme.Colours.text,
        fontWeight: 600,
        overflowWrap: 'anywhere' as const,
    },
    usageGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '16px',
    },
    usageCard: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '20px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    usageCardOverage: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '20px',
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '10px',
    },
    usageLabel: {
        opacity: 0.72,
    },
    billingOverageAlert: {
        ...Theme.Fonts.body,
        padding: '12px 14px',
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '8px',
    },
    billingOwnerSection: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '18px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    billingOwnerHeading: {
        ...Theme.Fonts.body,
        fontWeight: 700,
        margin: '0 0 4px 0',
    },
    billingOwnerControls: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '10px',
    },
    billingOwnerSelect: {
        ...Theme.Fonts.body,
        minWidth: '220px',
        padding: '10px 12px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        border: Theme.Colours.border,
        borderRadius: '6px',
    },
    migrationSection: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '20px',
        padding: '22px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    migrationFormGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        alignItems: 'start',
        gap: '18px',
    },
    migrationOfferControl: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        gap: '12px',
        padding: '18px',
        backgroundColor: Theme.Colours.background,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    migrationSwitchLabel: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        fontWeight: 600,
        cursor: 'pointer',
    },
    migrationOfferEnabledStatus: {
        ...Theme.Fonts.body,
        margin: '8px 0 0',
        color: '#28613b',
        fontWeight: 600,
    },
    migrationOfferStaleStatus: {
        ...Theme.Fonts.body,
        margin: '8px 0 0',
        color: '#785500',
        fontWeight: 600,
    },
    migrationOfferRecoveryStatus: {
        ...Theme.Fonts.body,
        margin: '8px 0 0',
        color: '#8a1c12',
        fontWeight: 600,
    },
    migrationField: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        gap: '8px',
    },
    migrationFieldset: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: 0,
        margin: 0,
        border: 0,
    },
    migrationRadioLabel: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    migrationCheckoutSummary: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '18px',
        padding: '18px',
        backgroundColor: Theme.Colours.background,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    migrationPrice: {
        ...Theme.Fonts.h2,
        display: 'block',
        margin: '4px 0',
    },
    packGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 320px))',
        gap: '16px',
    },
    packCard: {
        ...Theme.Fonts.body,
        padding: '18px',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        gap: '12px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    customRoleDeleteButton: {
        ...Theme.Fonts.body,
        border: 'none',
        background: 'transparent',
        color: '#b42318',
        cursor: 'pointer',
        padding: '6px',
    },
    searchContainer: { 
        display: 'flex', 
        margin: '0 0 16px 0', // top right bottom left
        gap: '12px',
        alignItems: 'center',
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        margin: '0 0 16px 0',
        gap: '12px',
        flexShrink: 0,
    },
    tableContainer: { 
        flexGrow: 1, 
        overflowY: 'auto' as const, 
        paddingBottom: '20px',
    },
    table: {
        width: '100%',
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
    },
    headerRow: {
        backgroundColor: Theme.Colours.tableHeader,
        color: Theme.Colours.text,
    },
    headerCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px 16px 24px', // top right bottom left
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
    },
    reportTemplateFileHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px 16px 24px',
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
        width: '40%',
    },
    actionsHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'right' as const,
        borderBottom: Theme.Colours.border,
        width: '82px',
    },
    tableCell: {
        ...Theme.Fonts.body,
        padding: '16px 24px 16px 24px', // top right bottom left
        borderBottom: Theme.Colours.border,
    },
    teamStatus: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: '#e8f4ec',
        color: '#28613b',
        fontSize: '12px',
        fontWeight: 600,
    },
    teamStatusSuspended: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: '#fff0ed',
        color: '#8a1c12',
        fontSize: '12px',
        fontWeight: 600,
    },
    teamVerificationStatus: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: '#e8f4ec',
        color: '#28613b',
        fontSize: '12px',
        fontWeight: 600,
    },
    teamVerificationPending: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: '#fff6d6',
        color: '#785500',
        fontSize: '12px',
        fontWeight: 600,
    },
    billingOwnerLabel: {
        display: 'block',
        marginTop: '5px',
        fontSize: '11px',
        opacity: 0.7,
    },
    actionsCell: {
        ...Theme.Fonts.body,
        padding: '10px 24px',
        borderBottom: Theme.Colours.border,
        textAlign: 'right' as const,
        width: '82px',
    },
    emptyTableCell: {
        ...Theme.Fonts.body,
        padding: '24px',
        borderBottom: Theme.Colours.border,
        color: Theme.Colours.text,
        opacity: 0.75,
        textAlign: 'center' as const,
    },
    dialogOverlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    dialog: {
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '12px',
        padding: '28px',
        minWidth: '420px',
        maxWidth: '520px',
        maxHeight: 'calc(100vh - 80px)',
        overflowY: 'auto' as const,
        border: Theme.Colours.border,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    dialogTitle: {
        ...Theme.Fonts.h2,
        margin: '0 0 24px 0',
        color: Theme.Colours.text,
    },
    seatUsageSummary: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '5px',
        marginBottom: '20px',
        padding: '12px 14px',
        color: Theme.Colours.text,
        backgroundColor: '#efeffd',
        border: '1px solid #d5d3f7',
        borderRadius: '8px',
    },
    seatLimitAlert: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '5px',
        marginBottom: '12px',
        padding: '12px 14px',
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '8px',
    },
    seatCapacityActions: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '10px',
        marginBottom: '20px',
        padding: '14px',
        backgroundColor: Theme.Colours.background,
        border: Theme.Colours.border,
        borderRadius: '8px',
    },
    seatCapacityControls: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '10px',
    },
    capacityInput: {
        ...Theme.Fonts.body,
        width: '86px',
        minHeight: '42px',
        margin: 0,
        padding: '8px 10px',
        textAlign: 'center' as const,
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        border: Theme.Colours.border,
        borderRadius: '6px',
        boxSizing: 'border-box' as const,
    },
    capacityLink: {
        ...Theme.Fonts.body,
        color: Theme.Colours.buttonBackground,
        fontWeight: 700,
        textDecoration: 'underline',
    },
    formField: {
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        color: Theme.Colours.text,
    },
    fieldHint: {
        ...Theme.Fonts.body,
        margin: 0,
        fontSize: '12px',
        lineHeight: 1.45,
        color: Theme.Colours.text,
        opacity: 0.7,
    },
    input: {
        ...Theme.Fonts.body,
        width: '100%',
        // Keep each dialog control aligned to the same baseline size.
        minHeight: '44px',
        margin: 0,
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    selectControl: {
        position: 'relative' as const,
        width: '100%',
    },
    selectInput: {
        // Removing Safari's native select chrome makes the explicit height reliable.
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        height: '44px',
        paddingRight: '44px',
        cursor: 'pointer',
    },
    selectChevron: {
        position: 'absolute' as const,
        top: '50%',
        right: '14px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: Theme.Colours.text,
        pointerEvents: 'none' as const,
        transform: 'translateY(-50%)',
    },
    textarea: {
        ...Theme.Fonts.body,
        width: '100%',
        minHeight: '120px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
        resize: 'vertical' as const,
    },
    fileInput: {
        ...Theme.Fonts.body,
        width: '100%',
        margin: 0,
        padding: '10px 0',
        color: Theme.Colours.text,
    },
    fileHint: {
        ...Theme.Fonts.body,
        fontSize: '12px',
        color: Theme.Colours.text,
        opacity: 0.7,
    },
    dialogButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '28px',
    },
}
